import { useState, useEffect, lazy, Suspense } from 'react';
import {
    ArrowLeft, ArrowRight, Database, Map,
    GitBranch, Rocket, Check, Loader2, Eye, Layers, ShieldAlert, AlertTriangle, Lock, Unlock, Save, ExternalLink, Globe, Users, Shield,
    UploadCloud, Zap, Clock
} from 'lucide-react';
import { useAuth } from '@/context/useAuth';
import { useNotification } from '@/context/NotificationContext';
import { apiGet, interactiveFetch } from '../api';
import { hasPermission } from '../utils/permissions';
import { PERMISSIONS } from '../constants/permissions';
import WorklistRunnerPage from './WorklistRunnerPage';
import { useBreadcrumbTitle } from '../hooks/useBreadcrumbTitle';

const STEPS = [
    { id: 1, label: 'Source',     icon: Database },
    { id: 2, label: 'Schema',     icon: Map },
    { id: 3, label: 'Access',     icon: GitBranch },
    { id: 4, label: 'Write-back', icon: UploadCloud },
    { id: 5, label: 'Publish',    icon: Rocket },
];

// ── Stable module-level component — MUST be outside WorklistEditorPage ──────
// Defining this inside the render function causes React to treat it as a new
// component type on every render, unmounting the input after each keystroke.
function UserPillSelect({ label, hint, users, selectedIds, onAdd, onRemove, searchVal, onSearch, excludeIds = [] }) {
    const filtered = users.filter(u =>
        !selectedIds.includes(u.id) &&
        !excludeIds.includes(u.id) &&
        (u.name || u.email || '').toLowerCase().includes(searchVal.toLowerCase())
    );
    return (
        <Field label={label}>
            {hint && <p className="text-[10px] text-gray-400 dark:text-slate-500 mb-2">{hint}</p>}
            <div className="min-h-[44px] flex flex-wrap gap-1.5 p-2.5 border border-gray-200 dark:border-slate-700 rounded-xl bg-gray-50/50 dark:bg-slate-800/40 focus-within:border-indigo-400 focus-within:ring-2 focus-within:ring-indigo-500/10 transition-all">
                {selectedIds.map(id => {
                    const u = users.find(x => x.id === id);
                    return (
                        <span key={id} className="inline-flex items-center gap-1 px-2.5 py-1 bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 rounded-lg text-xs font-semibold border border-indigo-200 dark:border-indigo-800/50">
                            <span className="w-4 h-4 rounded-full bg-indigo-500 text-white text-[9px] flex items-center justify-center font-bold shrink-0">
                                {(u?.name || u?.email || '?')[0].toUpperCase()}
                            </span>
                            {u?.name || u?.email || id}
                            <button type="button" onClick={() => onRemove(id)} className="ml-0.5 text-indigo-400 hover:text-red-500 transition-colors font-black text-[10px]">✕</button>
                        </span>
                    );
                })}
                <input
                    value={searchVal}
                    onChange={e => onSearch(e.target.value)}
                    placeholder={users.length === 0 ? 'Loading users…' : selectedIds.length === 0 ? 'Type to search users…' : 'Add more…'}
                    className="flex-1 min-w-[140px] bg-transparent text-xs text-gray-700 dark:text-slate-300 outline-none placeholder:text-gray-400"
                />
            </div>
            {searchVal && filtered.length > 0 && (
                <div className="mt-1 border border-gray-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-900 shadow-lg max-h-44 overflow-y-auto">
                    {filtered.slice(0, 10).map(u => (
                        <button key={u.id} type="button"
                            onMouseDown={e => { e.preventDefault(); onAdd(u.id); onSearch(''); }}
                            className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-indigo-50 dark:hover:bg-slate-800 text-left transition-colors">
                            <span className="w-6 h-6 rounded-full bg-indigo-500 text-white text-[10px] font-bold flex items-center justify-center shrink-0">
                                {(u.name || u.email || '?')[0].toUpperCase()}
                            </span>
                            <div>
                                <p className="text-xs font-semibold text-gray-800 dark:text-slate-200">{u.name || u.email}</p>
                                {u.name && <p className="text-[10px] text-gray-400 dark:text-slate-500">{u.email}</p>}
                            </div>
                        </button>
                    ))}
                </div>
            )}
            {searchVal && filtered.length === 0 && (
                <p className="mt-1 text-[11px] text-gray-400 px-1">
                    {users.length === 0 ? '⚠ Users not loaded — check permissions' : 'No matching users found'}
                </p>
            )}
        </Field>
    );
}

export default function WorklistEditorPage({ appId, onBack, onSaved, onDraftSaved }) {
    const isEdit = !!appId;
    const { token, user } = useAuth();
    const { addNotification } = useNotification();

    const [step, setStep] = useState(1);
    const [saving, setSaving] = useState(false);
    const [previewing, setPreviewing] = useState(false);  // Step 1 data-preview loading
    const [errors, setErrors] = useState({});
    const [showConfirm, setShowConfirm] = useState(false);
    const [showPreview, setShowPreview] = useState(false); // Step 4 full-screen preview modal
    const [savedId, setSavedId] = useState(appId || null);
    const [previewRows, setPreviewRows] = useState([]);
    const [datasources, setDatasources] = useState([]);
    const [schemaCollapsed, setSchemaCollapsed] = useState(true);

    // Form state
    const [dataModels, setDataModels] = useState([]);
    const [dmTables, setDmTables] = useState([]); // tables of selected data model
    const [flows, setFlows] = useState([]);
    const [roles, setRoles] = useState([]);
    const [users, setUsers] = useState([]);
    const visibleRoles = roles.filter(r => (user?.roleId === 'r1' || user?.roleId === 'r2') ? true : r.scope === 'Tenant');
    const [activePermissionMenu, setActivePermissionMenu] = useState(null);
    const [userSearchInclude, setUserSearchInclude] = useState('');
    const [userSearchExclude, setUserSearchExclude] = useState('');
    const [userSearchAssignee, setUserSearchAssignee] = useState('');

    const [form, setForm] = useState({
        name: '', description: '',
        source_type: 'data_model',          // default Phase 1: Data Model
        datasource_id: '', source_query: '', source_table: '',
        data_model_id: '', data_model_table: '',
        sharepoint_config: { site_hostname: '', site_path: '', drive_name: 'Documents', workbook_path: '', workbook_file: '', worksheet_name: 'Sheet1', tenant_id: '', client_id: '', client_secret: '' },
        schema_map: [],
        template_id: 'classic_table',
        workflow_config: { statuses: ['Open', 'In Progress', 'Pending Review', 'Closed'], transitions: {}, assignee_pool: [], system_columns: [] },
        writeback_mode: 'realtime',
        writeback_schedule: '',
        flow_id: '',
        writeback_sink: { type: 'same_as_source', write_mode: 'update_rows', target_type: 'table' },
        writeback_trigger: 'manual',
        writeback_fields: null,
        folder_name: '', shared_with: ['*'],
        publish_scope: 'internal',
        include_users: [],                   // specific user IDs granted access
        exclude_users: [],                   // specific user IDs denied access
        record_visibility: 'all',            // 'all' | 'assigned'
        assignee_pool: [],                   // user IDs who can be assigned items
        default_assignee: '',                // user ID or '' for None
        allow_add_entry: 'all',              // 'all' | 'admin' | 'none' — who can add new records
        default_sort_col: '',                // field key to sort by default
        default_sort_dir: 'asc',             // 'asc' | 'desc'
    });

    useBreadcrumbTitle(form.name !== '' ? form.name : null);

    const hasAssigneeCol = form.workflow_config?.system_columns?.includes('assignee');

    // File-based datasource type IDs (CSV=9, Excel=10, JSON=11, Parquet=12)
    const FILE_DS_TYPES = new Set([9, 10, 11, 12]);
    const selectedDs = datasources.find(d => d.id === form.datasource_id);
    const selectedDsIsFile = selectedDs ? FILE_DS_TYPES.has(selectedDs.type) : false;

    useEffect(() => {
        apiGet('/datasources', token).then(d => setDatasources(Array.isArray(d) ? d : [])).catch(() => addNotification('Failed to load datasources', 'error'));
        apiGet('/datamodels/', token).then(d => setDataModels(Array.isArray(d) ? d : [])).catch(() => addNotification('Failed to load data models', 'error'));
        apiGet('/flows', token).then(d => setFlows(Array.isArray(d) ? d : [])).catch(() => addNotification('Failed to load flows', 'error'));
        apiGet('/roles', token).then(r => setRoles(Array.isArray(r) ? r : [])).catch(() => addNotification('Failed to load roles', 'error'));
        apiGet('/users/', token).then(u => setUsers(Array.isArray(u) ? u : [])).catch(() => {});

        if (isEdit) {
            interactiveFetch(`/worklists/${appId}`, token, { method: 'GET' }).then(async d => {
                if (d) {
                    setForm(prev => {
                        const loadedScope = d.publish_scope || (
                            d.status === 'draft' ? 'draft' :
                            (d.shared_with?.includes('*') ? 'public' : 'internal')
                        );
                        return {
                            ...prev, ...d,
                            publish_scope: loadedScope,
                            sharepoint_config: d.sharepoint_config || prev.sharepoint_config,
                            schema_map: Array.isArray(d.schema_map) ? d.schema_map : [],
                            workflow_config: d.workflow_config || prev.workflow_config,
                        };
                    });
                    if (d.source_type === 'data_model' && d.data_model_id) {
                        const dm = await apiGet(`/datamodels/${d.data_model_id}`, token).catch(() => null);
                        if (Array.isArray(dm?.tables)) setDmTables(dm.tables.map(t => t.name).filter(Boolean));
                        if (d.data_model_table) {
                            try {
                                setPreviewing(true);
                                const sql = `SELECT * FROM "${d.data_model_table}" LIMIT 10`;
                                const res = await interactiveFetch(`/datamodels/${d.data_model_id}/query`, token, {
                                    method: 'POST',
                                    body: JSON.stringify({ sql })
                                });
                                const rows = res?.rows || [];
                                setPreviewRows(Array.isArray(rows) ? rows : []);
                            } catch (err) {
                                console.error("Edit mode preview load failed:", err);
                            } finally {
                                setPreviewing(false);
                            }
                        }
                    }
                    if (d.datasource_id && (d.source_query || d.source_table)) {
                        try {
                            setPreviewing(true);
                            const rows = await interactiveFetch('/worklists/preview-source', token, {
                                method: 'POST',
                                body: JSON.stringify({
                                    datasource_id: d.datasource_id, source_query: d.source_query || null,
                                    source_table: d.source_table || null, limit: 10,
                                })
                            });
                            setPreviewRows(Array.isArray(rows) ? rows : []);
                        } catch { }
                        finally { setPreviewing(false); }
                    }
                }
            }).catch(() => addNotification('Failed to load worklist config', 'error'));
        }
    }, [token]);

    // When data model changes, load its tables
    const handleDataModelChange = async (modelId) => {
        patch('data_model_id', modelId);
        patch('data_model_table', '');
        setDmTables([]);
        if (!modelId) return;
        // DatabaseOut.tables is a flat list of TableSchema objects with a 'name' field
        const dm = await apiGet(`/datamodels/${modelId}`, token).catch(() => null);
        if (Array.isArray(dm?.tables)) {
            setDmTables(dm.tables.map(t => t.name).filter(Boolean));
        }
    };

    const handleGridRowChange = (index, key, value) => {
        setForm(f => {
            const next = [...(f.schema_map || [])];
            const row = next[index];
            const updated = { ...row, [key]: value };
            // Auto-populate options from preview data when switching to Drop-Down / Multi-Select
            if (key === 'field_type' && (value === 'Drop-Down' || value === 'Multi-Select') && !row.options) {
                const colName = row.source_field;
                const uniqueVals = [...new Set(
                    previewRows.map(r => r[colName]).filter(v => v != null && String(v).trim() !== '')
                )].map(String).sort();
                if (uniqueVals.length > 0) {
                    updated.options = uniqueVals.join(', ');
                }
            }
            next[index] = updated;
            return { ...f, schema_map: next };
        });
    };

    // Status is now platform-managed — auto-detect removed
    // Admins define statuses manually in the System Columns > Workflow Statuses section

    // Smart 3-way schema sync: triggered whenever preview data arrives
    // Rules (product-grade):
    //   1. Strip internal __ keys (pk, sp_row_index) — they are not real source columns
    //   2. Preserve ALL existing config (core_mapping, field_type, options, etc.) for known columns
    //   3. New columns from source → appended as unconfirmed rows
    //   4. Columns that were previously saved but are NO LONGER in the source → marked with [Removed] suffix
    //      so the admin can consciously decide what to do, nothing is silently dropped
    useEffect(() => {
        if (previewRows.length > 0) {
            const sourceCols = Object.keys(previewRows[0]).filter(c => !c.startsWith('__'));
            setForm(f => {
                const existingMap = f.schema_map || [];
                const existingFieldSet = new Set(existingMap.map(r => r.source_field));
                const sourceColSet = new Set(sourceCols);

                // Add brand-new columns (not in saved map at all)
                const newRows = sourceCols
                    .filter(c => !existingFieldSet.has(c))
                    .map(c => ({
                        is_selected: true,
                        source_field: c,
                        core_mapping: 'None',
                        is_read_only: true,
                        field_type: 'As-Is',
                        options: '',
                        is_remarks_column: false,
                        update_flag: 'NA',
                    }));

                // Mark stale columns (were saved but no longer in source)
                const updatedExisting = existingMap.map(r => {
                    const wasRemoved = !sourceColSet.has(r.source_field) && !r.source_field?.endsWith(' [Removed]');
                    if (wasRemoved) {
                        return { ...r, is_selected: false, _stale: true, source_field: `${r.source_field} [Removed]` };
                    }
                    return r;
                });

                const merged = [...updatedExisting, ...newRows];
                // Only update if something actually changed
                if (newRows.length > 0 || updatedExisting.some(r => r._stale)) {
                    return { ...f, schema_map: merged };
                }
                return f;
            });
        }
    }, [previewRows]);

    const patch = (key, val) => setForm(f => ({ ...f, [key]: val }));
    const patchWf = (key, val) => setForm(f => ({
        ...f, workflow_config: { ...f.workflow_config, [key]: val }
    }));
    const patchSp = (key, val) => setForm(f => ({
        ...f, sharepoint_config: { ...f.sharepoint_config, [key]: val }
    }));

    const handlePreview = async () => {
        setPreviewing(true);
        try {
            let rows;
            if (form.source_type === 'data_model') {
                if (!form.data_model_id || !form.data_model_table) {
                    addNotification('Select a Data Model and a table first', 'warning');
                    return;
                }
                 const sql = `SELECT * FROM "${form.data_model_table}" LIMIT 10`;
                 const res = await interactiveFetch(`/datamodels/${form.data_model_id}/query`, token, {
                     method: 'POST',
                     body: JSON.stringify({ sql })
                 });
                 rows = res?.rows || [];
             } else {
                if (!form.datasource_id) {
                    addNotification('Select a datasource first', 'warning');
                    return;
                }
                // File-based datasources don't need query or table
                const _ds = datasources.find(d => d.id === form.datasource_id);
                const _isFile = _ds ? FILE_DS_TYPES.has(_ds.type) : false;
                if (!_isFile && !form.source_query && !form.source_table) {
                    addNotification('Select a datasource and provide a query or table name', 'warning');
                    return;
                }
                const res = await interactiveFetch('/datasources/query', token, {
                    method: 'POST',
                    body: JSON.stringify({
                        datasource_id: form.datasource_id,
                        source_query: form.source_query || null,
                        source_table: form.source_table || null,
                        limit: 10,
                    })
                });
                rows = res?.rows || [];
            }
            setPreviewRows(Array.isArray(rows) ? rows : []);
            if (!rows?.length) addNotification('Query returned no rows', 'warning');
        } catch (e) {
            addNotification('Preview failed: ' + (e?.message || ''), 'error');
        } finally {
            setPreviewing(false);
        }
    };


    // ── Step Validation ──────────────────────────────────────────────────────
    const validateStep = (s) => {
        const e = {};
        if (s === 1) {
            if (!form.name.trim()) e.name = 'Worklist name is required';
            if (form.source_type === 'data_model' && !form.data_model_id) e.source = 'Please select a data model';
            if (form.source_type === 'sql' && !form.datasource_id) e.source = 'Please select a datasource';
        }
        if (s === 2) {
            if (form.schema_map?.length > 0 && !form.schema_map?.find(r => r.core_mapping === 'Primary Key' && r.is_selected)) {
                e.pk = 'Primary Key mapping is required';
            } else if (!form.schema_map || form.schema_map.length === 0) {
                e.pk = 'Please go back to Step 1 and preview your data to load columns.';
            }
        }
        if (s === 2) {
            // Validate workflow statuses if status system column is enabled
            const sysColsEnabled = form.workflow_config?.system_columns || [];
            if (sysColsEnabled.includes('status') && !form.workflow_config?.statuses?.length) {
                e.statuses = 'At least one workflow status is required when Status column is enabled';
            }
        }
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleNext = () => {
        if (validateStep(step)) {
            setStep(s => s + 1);
        } else {
            addNotification('Please fix the highlighted errors before continuing', 'warning');
        }
    };

    const handleSave = async () => {
        if (!validateStep(4)) {
            addNotification('Please review your configuration', 'warning');
            return;
        }
        setShowConfirm(false);
        setSaving(true);
        try {
            const payload = { ...form };
            delete payload._wbFieldsOpen;  // UI-only state, don't send to backend
            delete payload._schedTab;      // UI-only: schedule tab selection
            delete payload._schedIntervalVal;  // UI-only: interval picker value
            delete payload._schedIntervalUnit; // UI-only: interval picker unit
            if (payload.source_type === 'sharepoint' && payload.writeback_mode === 'sql_table') {
                payload.writeback_mode = 'platform_managed';
            }

            if (isEdit) {
                await interactiveFetch(`/worklists/${appId}`, token, { method: 'PATCH', body: JSON.stringify(payload) });
                addNotification('Worklist updated', 'success');
                if (onBack) onBack();
            } else {
                const res = await interactiveFetch('/worklists', token, { method: 'POST', body: JSON.stringify(payload) });
                addNotification('Worklist created!', 'success');
                if (onSaved) onSaved(res.id);
            }
        } catch (err) {
            addNotification(`Save failed: ${err?.message || 'Unknown error'}`, 'error');
        } finally {
            setSaving(false);
        }
    };



    const colsFromPreview = previewRows.length > 0 ? Object.keys(previewRows[0]).filter(c => !c.startsWith('__')) : [];

    // ── Permission Guard ─────────────────────────────────────────────────────
    const canManageWorklists = hasPermission(user, PERMISSIONS.WORKLISTS_UPDATE);
    if (!canManageWorklists) {
        return (
            <div className="flex flex-col items-center justify-center h-full bg-gray-50 dark:bg-slate-950 gap-4">
                <div className="p-4 rounded-full bg-red-50 dark:bg-red-900/20">
                    <ShieldAlert className="w-10 h-10 text-red-500" />
                </div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-slate-100">Access Denied</h2>
                <p className="text-sm text-gray-500 dark:text-slate-400 text-center max-w-sm">
                    You don't have permission to create or edit worklists. Please contact your administrator.
                </p>
                <button onClick={onBack} className="mt-2 px-4 py-2 rounded-xl bg-gray-200 dark:bg-slate-800 text-sm font-medium text-gray-700 dark:text-slate-300 hover:bg-gray-300 dark:hover:bg-slate-700 transition-colors">
                    Go Back
                </button>
            </div>
        );
    }

    return (
        <>
        <div className="flex flex-col h-full bg-gray-50 dark:bg-slate-950">
            {/* Confirmation Dialog */}
            {showConfirm && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 max-w-md w-full shadow-2xl space-y-4 border border-gray-100 dark:border-slate-800">
                        <div className="flex items-center gap-3">
                            <div className="p-2.5 rounded-xl bg-indigo-50 dark:bg-indigo-900/30">
                                <AlertTriangle className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                            </div>
                            <div>
                                <h3 className="text-sm font-bold text-gray-900 dark:text-slate-100">
                                    {form.publish_scope === 'draft'
                                        ? (isEdit ? 'Save Draft?' : 'Create Draft?')
                                        : (isEdit ? 'Save Changes?' : 'Publish Worklist?')}
                                </h3>
                                <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
                                    {form.publish_scope === 'draft'
                                        ? "This will save your configuration as a draft. Users won't see it yet."
                                        : "This will make the worklist available to assigned roles."}
                                </p>
                            </div>
                        </div>
                        <div className="p-3 bg-gray-50 dark:bg-slate-800/50 rounded-xl space-y-1.5">
                            <p className="text-xs text-gray-600 dark:text-slate-300"><span className="font-semibold">Name:</span> {form.name}</p>
                            <p className="text-xs text-gray-600 dark:text-slate-300"><span className="font-semibold">Source:</span> {form.source_type}</p>
                            <p className="text-xs text-gray-600 dark:text-slate-300"><span className="font-semibold">Template:</span> Classic Worklist</p>
                        </div>
                        <div className="flex items-center gap-2 pt-1">
                            <button onClick={() => setShowConfirm(false)} className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 dark:border-slate-700 text-sm font-medium text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors">
                                Cancel
                            </button>
                            <button onClick={handleSave} disabled={saving} className="flex-1 px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold shadow-sm transition-all disabled:opacity-60 flex items-center justify-center gap-2">
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
                                {form.publish_scope === 'draft'
                                    ? (isEdit ? 'Save Draft' : 'Create Draft')
                                    : (isEdit ? 'Save & Publish' : 'Create & Publish')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Main Layout: Sidebar + Content */}
            <div className="flex flex-1 overflow-hidden">

                {/* ── Left Sidebar — Step Navigation ── */}
                <div className="flex-none w-[240px] bg-white dark:bg-slate-900 border-r border-gray-200 dark:border-slate-800 flex flex-col">
                    {/* Sidebar Header */}
                    <div className="px-5 pt-6 pb-5 border-b border-gray-200 dark:border-slate-800/60">
                        <button onClick={onBack} className="flex items-center gap-2 text-gray-400 hover:text-gray-700 dark:text-slate-400 dark:hover:text-white text-xs font-medium transition-colors mb-4 group">
                            <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-0.5 transition-transform" />
                            <span>Back to Studio</span>
                        </button>
                        <h1 className="text-base font-bold text-gray-900 dark:text-white leading-tight">{isEdit ? 'Edit Worklist' : 'New Worklist'}</h1>
                        <p className="text-[11px] text-gray-400 dark:text-slate-500 mt-1">Configure in {STEPS.length} steps</p>
                    </div>

                    {/* Step List */}
                    <nav className="flex-1 px-3 py-4 space-y-1">
                        {STEPS.map((s) => {
                            const Icon = s.icon;
                            const done = step > s.id;
                            const active = step === s.id;
                            return (
                                <button
                                    key={s.id}
                                    onClick={() => done && setStep(s.id)}
                                    disabled={!done && !active}
                                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 group ${
                                        active
                                            ? 'bg-indigo-50 dark:bg-indigo-600/20 text-indigo-700 dark:text-indigo-400 shadow-[inset_3px_0_0_0_rgb(99,102,241)]'
                                            : done
                                                ? 'text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800/60 hover:text-gray-900 dark:hover:text-white cursor-pointer'
                                                : 'text-gray-300 dark:text-slate-600 cursor-not-allowed'
                                    }`}
                                >
                                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold shrink-0 transition-all ${
                                        active
                                            ? 'bg-indigo-600 text-white shadow-md shadow-indigo-600/30'
                                            : done
                                                ? 'bg-emerald-100 dark:bg-emerald-600/20 text-emerald-600 dark:text-emerald-400'
                                                : 'bg-gray-100 dark:bg-slate-800/60 text-gray-300 dark:text-slate-600'
                                    }`}>
                                        {done ? <Check className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
                                    </div>
                                    <div className="text-left">
                                        <p className={`text-xs font-semibold ${active ? 'text-indigo-700 dark:text-indigo-300' : done ? 'text-gray-700 dark:text-slate-300' : 'text-gray-300 dark:text-slate-600'}`}>{s.label}</p>
                                        <p className={`text-[10px] mt-0.5 ${active ? 'text-indigo-400' : 'text-gray-400 dark:text-slate-600'}`}>Step {s.id}</p>
                                    </div>
                                    {active && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse" />}
                                </button>
                            );
                        })}
                    </nav>

                </div>


                {/* ── Right Content Area ── */}
                <div className="flex-1 flex flex-col overflow-hidden bg-gray-50 dark:bg-slate-950">
                    {/* Content */}
                    <div className="flex-1 overflow-y-auto p-8 w-full transition-all duration-500 ease-in-out">
                {step === 1 && (
                    <StepCard title="Data Source" desc="Where does your worklist data come from?">
                        <Field label="Worklist Name *">
                            <input className={`${input} ${errors.name ? 'border-red-400 dark:border-red-500 ring-2 ring-red-500/10' : ''}`} placeholder="e.g. AR Claims Review" value={form.name} onChange={e => { patch('name', e.target.value); if (errors.name) setErrors(prev => ({ ...prev, name: undefined })); }} />
                            {errors.name && <p className="text-[10px] text-red-500 font-semibold mt-1">{errors.name}</p>}
                        </Field>
                        <Field label="Description">
                            <textarea className={`${input} h-20 resize-none`} placeholder="What is this worklist for?" value={form.description} onChange={e => patch('description', e.target.value)} />
                        </Field>
                        <Field label="Source Type">
                            <div className="grid grid-cols-3 gap-3">
                                {[
                                    { v: 'data_model', l: 'Data Model', icon: <Layers className="w-4 h-4" /> },
                                    { v: 'sql', l: 'SQL / Database', icon: <Database className="w-4 h-4" /> },
                                    { v: 'sharepoint', l: 'SharePoint Excel', icon: <span className="text-[10px] font-extrabold uppercase font-sans tracking-wide">SP</span> },
                                ].map(({ v, l, icon }) => (
                                    <button key={v} type="button" onClick={() => {
                                        patch('source_type', v);
                                        if (v === 'sharepoint') {
                                            patch('writeback_mode', 'sharepoint_adapter');
                                        } else if (v !== 'sql') {
                                            patch('writeback_mode', 'platform_managed');
                                        }
                                    }}
                                        className={`flex flex-col items-center gap-2 py-4 rounded-2xl border-2 text-xs font-bold transition-all duration-300 hover:-translate-y-0.5 hover:scale-[1.02] active:scale-[0.98] ${form.source_type === v
                                                ? 'border-indigo-500 bg-indigo-50/50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 shadow-[0_0_20px_rgba(99,102,241,0.12)]'
                                                : 'border-gray-200 dark:border-slate-800/80 text-gray-500 dark:text-slate-400 hover:border-indigo-350 dark:hover:border-indigo-800/80 bg-white/40 dark:bg-slate-900/40'
                                            }`}>
                                        <div className={`p-2.5 rounded-xl transition-all ${form.source_type === v ? 'bg-indigo-600 text-white shadow-md shadow-indigo-500/25' : 'bg-gray-100 dark:bg-slate-800 text-gray-400 dark:text-slate-500'}`}>
                                            {icon}
                                        </div>
                                        <span>{l}</span>
                                    </button>
                                ))}
                            </div>
                        </Field>

                        {/* ── Data Model source ── */}
                        {form.source_type === 'data_model' && (<>
                            <Field label="Data Model *">
                                <select className={input} value={form.data_model_id} onChange={e => handleDataModelChange(e.target.value)}>
                                    <option value="">— Select Data Model —</option>
                                    {dataModels.map(dm => <option key={dm.id} value={dm.id}>{dm.name}</option>)}
                                </select>
                            </Field>
                            {dmTables.length > 0 && (
                                <Field label="Table / View *">
                                    <select className={input} value={form.data_model_table} onChange={e => patch('data_model_table', e.target.value)}>
                                        <option value="">— Select table —</option>
                                        {dmTables.map(t => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                </Field>
                            )}
                            {form.data_model_id && form.data_model_table && (
                                <button onClick={async () => {
                                    setPreviewing(true);
                                    try {
                                         const sql = `SELECT * FROM "${form.data_model_table}" LIMIT 10`;
                                         const res = await interactiveFetch(`/datamodels/${form.data_model_id}/query`, token, {
                                             method: 'POST',
                                             body: JSON.stringify({ sql })
                                         });
                                         const rows = res?.rows || [];
                                        setPreviewRows(Array.isArray(rows) ? rows : []);
                                        if (!rows.length) addNotification('No rows returned', 'warning');
                                    } catch (e) { addNotification('Preview failed: ' + (e?.message || ''), 'error'); }
                                    finally { setPreviewing(false); }
                                }} disabled={previewing}
                                    className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 rounded-xl text-sm font-medium text-gray-700 dark:text-slate-200 transition-colors">
                                    {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                                    Preview Data Model
                                </button>
                            )}
                        </>)}

                        {/* ── SQL / File source ── */}
                        {form.source_type === 'sql' && (<>
                            <Field label="Datasource *">
                                <select className={input} value={form.datasource_id} onChange={e => patch('datasource_id', e.target.value)}>
                                    <option value="">— Select datasource —</option>
                                    {datasources.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                                </select>
                            </Field>
                            {selectedDsIsFile ? (
                                /* File datasource — no SQL needed, just preview */
                                <>
                                    <div className="flex items-center gap-3 p-4 bg-indigo-50 dark:bg-indigo-900/10 border border-indigo-200 dark:border-indigo-800/30 rounded-xl">
                                        <Database className="w-5 h-5 text-indigo-500 shrink-0" />
                                        <p className="text-sm text-indigo-700 dark:text-indigo-300">
                                            <strong>{selectedDs?.name}</strong> is a file-based datasource. Click Preview to load its data directly — no SQL query needed.
                                        </p>
                                    </div>
                                    <button onClick={handlePreview} disabled={previewing}
                                        className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 rounded-xl text-sm font-medium text-gray-700 dark:text-slate-200 transition-colors">
                                        {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                                        Preview File Data
                                    </button>
                                </>
                            ) : (
                                /* SQL database — show query / table fields */
                                <>
                                    <Field label="SQL Query">
                                        <textarea className={`${input} h-28 resize-none font-mono text-sm`} placeholder="SELECT * FROM dbo.ARClaims" value={form.source_query} onChange={e => patch('source_query', e.target.value)} />
                                    </Field>
                                    <Field label="— or — Table / View Name">
                                        <input className={input} placeholder="dbo.ARClaims" value={form.source_table} onChange={e => patch('source_table', e.target.value)} />
                                    </Field>
                                    <button onClick={handlePreview} disabled={previewing}
                                        className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 rounded-xl text-sm font-medium text-gray-700 dark:text-slate-200 transition-colors">
                                        {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                                        Preview Data
                                    </button>
                                </>
                            )}
                        </>)}

                        {/* ── SharePoint Excel source ── */}
                        {form.source_type === 'sharepoint' && (<>
                            <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide">SharePoint Location</p>
                            <Field label="SharePoint Domain *">
                                <div className="flex items-center gap-2">
                                    <input className={input} placeholder="aidastech0" value={form.sharepoint_config.site_hostname} onChange={e => patchSp('site_hostname', e.target.value)} />
                                    <span className="text-sm text-gray-400 dark:text-slate-500 whitespace-nowrap">.sharepoint.com</span>
                                </div>
                            </Field>
                            <Field label="Site Name *">
                                <input className={input} placeholder="AIDASPlatform" value={form.sharepoint_config.site_path} onChange={e => patchSp('site_path', e.target.value)} />
                            </Field>
                            <Field label="SharePoint Folder Path">
                                <input className={input} placeholder="aidas_apps_demo/patients_charts" value={form.sharepoint_config.workbook_path} onChange={e => patchSp('workbook_path', e.target.value)} />
                                <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">Folder path INSIDE the document library (e.g., /GenAI/Internal). Do NOT include the library name.</p>
                            </Field>
                            <Field label="Workbook File Name *">
                                <input className={input} placeholder="ARClaims.xlsx" value={form.sharepoint_config.workbook_file} onChange={e => patchSp('workbook_file', e.target.value)} />
                            </Field>
                            <Field label="Worksheet Name">
                                <input className={input} placeholder="Sheet1" value={form.sharepoint_config.worksheet_name} onChange={e => patchSp('worksheet_name', e.target.value)} />
                            </Field>
                            <div className="border-t border-gray-200 dark:border-slate-700 pt-3 mt-2">
                                <div className="flex items-center justify-between">
                                    <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide">Azure AD App Registration</p>
                                    <button type="button" onClick={async () => {
                                        try {
                                            const res = await interactiveFetch('/datasources/sharepoint-creds', token, { method: 'GET' });
                                            if (res?.tenant_id) {
                                                patchSp('tenant_id', res.tenant_id || '');
                                                patchSp('client_id', res.client_id || '');
                                                patchSp('client_secret', res.client_secret || '');
                                                addNotification('Credentials loaded from integration', 'success');
                                            } else {
                                                addNotification('No SharePoint integration found. Enter credentials manually.', 'warning');
                                            }
                                        } catch { addNotification('Could not load integration keys', 'error'); }
                                    }} className="text-xs text-indigo-500 hover:text-indigo-700 dark:text-indigo-400 font-medium">
                                        Load from Integration
                                    </button>
                                </div>
                            </div>
                            <Field label="Azure Tenant ID *">
                                <input className={input} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" value={form.sharepoint_config.tenant_id} onChange={e => patchSp('tenant_id', e.target.value)} />
                            </Field>
                            <Field label="Client ID *">
                                <input className={input} placeholder="App (client) ID" value={form.sharepoint_config.client_id} onChange={e => patchSp('client_id', e.target.value)} />
                            </Field>
                            <Field label="Client Secret *">
                                <input type="password" className={input} placeholder="Client secret value" value={form.sharepoint_config.client_secret} onChange={e => patchSp('client_secret', e.target.value)} />
                            </Field>
                            <button onClick={async () => {
                                const sp = form.sharepoint_config;
                                if (!sp.site_hostname || !sp.site_path) { addNotification('Enter SharePoint Domain and Site Name', 'warning'); return; }
                                if (!sp.workbook_file) { addNotification('Enter the workbook file name', 'warning'); return; }
                                if (!sp.tenant_id || !sp.client_id || !sp.client_secret) { addNotification('Enter Azure AD credentials', 'warning'); return; }
                                setPreviewing(true);
                                try {
                                    const fullPath = sp.workbook_path
                                        ? `${sp.workbook_path.replace(/\/$/,'')}/${sp.workbook_file}`
                                        : sp.workbook_file;
                                    const res = await interactiveFetch('/datasources/sharepoint-preview', token, {
                                        method: 'POST',
                                        body: JSON.stringify({
                                            sharepoint_domain: sp.site_hostname,
                                            site_name: sp.site_path,
                                            drive_name: sp.drive_name || 'Documents',
                                            workbook_path: fullPath,
                                            worksheet_name: sp.worksheet_name || 'Sheet1',
                                            azure_tenant_id: sp.tenant_id,
                                            client_id: sp.client_id,
                                            client_secret: sp.client_secret,
                                            limit: 10,
                                        })
                                    });
                                    const rows = res?.rows || [];
                                    setPreviewRows(Array.isArray(rows) ? rows : []);
                                    if (!rows.length) addNotification('No rows returned from workbook', 'warning');
                                } catch (e) { addNotification('SharePoint preview failed: ' + (e?.message || ''), 'error'); }
                                finally { setPreviewing(false); }
                            }} disabled={previewing}
                                className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 rounded-xl text-sm font-medium text-gray-700 dark:text-slate-200 transition-colors">
                                {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                                Preview SharePoint Data
                            </button>
                        </>)}

                        {previewRows.length > 0 && (
                            <div className="mt-4 space-y-2">
                                <label className="block text-[10px] tracking-wider font-extrabold uppercase text-gray-500">Live Connection Preview (First 5 Rows)</label>
                                <div className="overflow-x-auto rounded-xl border border-gray-100 dark:border-slate-800/80 bg-gray-50/30 dark:bg-slate-900/50 shadow-inner">
                                    <table className="text-xs w-full border-collapse">
                                        <thead>
                                            <tr className="bg-gray-100/80 dark:bg-slate-800/60 border-b border-gray-200 dark:border-slate-850">
                                                {Object.keys(previewRows[0]).map(c => (
                                                    <th key={c} className="px-4 py-2.5 text-left font-bold text-gray-500 dark:text-slate-400 tracking-wide">{c}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {previewRows.slice(0, 5).map((r, i) => (
                                                <tr key={i} className="border-b border-gray-100/50 dark:border-slate-800/50 hover:bg-gray-100/20 dark:hover:bg-slate-800/25 transition-colors">
                                                    {Object.values(r).map((v, j) => (
                                                        <td key={j} className="px-4 py-2.5 text-gray-700 dark:text-slate-300 font-mono text-[11px] truncate max-w-[140px]">{String(v ?? '')}</td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </StepCard>
                )}

                {step === 2 && (
    <StepCard title="Schema Mapping & Configuration" desc="Map your source data fields to the system core fields for processing.">
        {errors.pk && (
            <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800/30 rounded-xl">
                <AlertTriangle className="w-5 h-5 text-red-500 shrink-0" />
                <p className="text-sm text-red-700 dark:text-red-400 font-medium">{errors.pk}</p>
            </div>
        )}
        {colsFromPreview.length === 0 && (!form.schema_map || form.schema_map.length === 0) && (
            <div className="flex items-center gap-3 p-4 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/30 rounded-xl">
                <Eye className="w-5 h-5 text-amber-500 shrink-0" />
                <p className="text-sm text-amber-700 dark:text-amber-300">Go back to <strong>Step 1</strong> and preview your data first to load columns.</p>
            </div>
        )}

        {form.schema_map?.length > 0 && (
            <div className="space-y-3">
                {/* Header row — column labels */}
                <div className="flex items-center px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-slate-500">
                    <div className="w-10 text-center shrink-0">
                        <input type="checkbox"
                            checked={form.schema_map.length > 0 && form.schema_map.every(r => r.is_selected)}
                            onChange={(e) => {
                                const val = e.target.checked;
                                setForm(f => ({ ...f, schema_map: f.schema_map.map(r => ({ ...r, is_selected: val })) }));
                            }}
                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 h-3.5 w-3.5"
                        />
                    </div>
                    <div className="flex-1 min-w-[120px] px-3">Field Name</div>
                    <div className="w-[180px] px-3">Core Mapping</div>
                    <div className="w-[130px] px-3 text-center">Editability</div>
                    <div className="w-[180px] px-3">Field Type</div>
                </div>

                {/* Schema rows as cards */}
                <div className="space-y-2">
                    {form.schema_map.map((row, idx) => {
                        const isStale = row.source_field?.endsWith(' [Removed]');
                        return (
                            <div key={row.source_field + idx}
                                className={`flex flex-wrap items-center px-4 py-3.5 rounded-xl border transition-all ${
                                    isStale ? 'bg-red-50/50 dark:bg-red-900/10 border-red-200 dark:border-red-800/30' :
                                    !row.is_selected ? 'opacity-50 bg-gray-50 dark:bg-slate-800/30 border-gray-200 dark:border-slate-700/50' :
                                    'bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-700 hover:border-indigo-300 dark:hover:border-indigo-700 hover:shadow-sm'
                                }`}>
                                {/* Checkbox */}
                                <div className="w-10 text-center shrink-0">
                                    <input type="checkbox"
                                        checked={row.is_selected}
                                        onChange={e => handleGridRowChange(idx, 'is_selected', e.target.checked)}
                                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 h-4 w-4"
                                    />
                                </div>
                                {/* Field Name */}
                                <div className="flex-1 min-w-[120px] px-3">
                                    <span className={`font-medium text-sm ${
                                        isStale ? 'text-red-500 line-through' :
                                        row.is_selected ? 'text-gray-800 dark:text-slate-200' : 'text-gray-400'
                                    }`}>{row.source_field}</span>
                                    {isStale && <span className="ml-1 text-[9px] text-red-500 font-semibold">Not in source</span>}
                                </div>
                                {/* Core Mapping */}
                                <div className="w-[180px] px-3">
                                    <select
                                        value={row.core_mapping || 'None'}
                                        onChange={e => handleGridRowChange(idx, 'core_mapping', e.target.value)}
                                        disabled={!row.is_selected}
                                        className="w-full bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg px-2.5 py-2 text-xs text-gray-700 dark:text-slate-300 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-400 outline-none disabled:opacity-40"
                                    >
                                        <optgroup label="— System Fields —">
                                            <option value="None">None</option>
                                            <option value="Primary Key">🔑 Primary Key</option>
                                        </optgroup>
                                    </select>
                                </div>
                                {/* Editability Toggle */}
                                <div className="w-[130px] px-3 flex items-center justify-center">
                                    <label className="inline-flex items-center gap-2 cursor-pointer">
                                        <div className={`relative w-10 h-5 rounded-full transition-colors ${
                                            row.is_read_only ? 'bg-gray-300 dark:bg-slate-600' : 'bg-emerald-500'
                                        } ${!row.is_selected ? 'opacity-40 cursor-not-allowed' : ''}`}
                                            onClick={() => row.is_selected && handleGridRowChange(idx, 'is_read_only', !row.is_read_only)}>
                                            <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                                                row.is_read_only ? 'translate-x-0.5' : 'translate-x-[22px]'
                                            }`} />
                                        </div>
                                        <span className={`text-[11px] font-semibold whitespace-nowrap ${
                                            row.is_read_only ? 'text-gray-500 dark:text-slate-400' : 'text-emerald-600 dark:text-emerald-400'
                                        }`}>{row.is_read_only ? 'Read-Only' : 'Editable'}</span>
                                    </label>
                                </div>
                                {/* Field Type */}
                                <div className="w-[180px] px-3">
                                    <select
                                        value={row.field_type || 'As-Is'}
                                        onChange={e => handleGridRowChange(idx, 'field_type', e.target.value)}
                                        disabled={!row.is_selected}
                                        className="w-full bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg px-2.5 py-2 text-xs text-gray-700 dark:text-slate-300 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-400 outline-none disabled:opacity-40"
                                    >
                                        <optgroup label="— Text —">
                                            <option value="As-Is">As-Is (Raw)</option>
                                            <option value="Text Box">Text Box</option>
                                            <option value="Text Area">Text Area (Multi-line)</option>
                                            <option value="Rich Text">Rich Text</option>
                                            <option value="URL">URL / Link</option>
                                            <option value="Email">Email</option>
                                            <option value="Phone">Phone Number</option>
                                        </optgroup>
                                        <optgroup label="— Numbers —">
                                            <option value="Whole Number">Whole Number</option>
                                            <option value="Decimal">Decimal</option>
                                            <option value="Currency">Currency</option>
                                            <option value="Percentage">Percentage (%)</option>
                                        </optgroup>
                                        <optgroup label="— Date / Time —">
                                            <option value="Date">Date</option>
                                            <option value="Date Time">Date + Time</option>
                                            <option value="Time">Time Only</option>
                                            <option value="Duration">Duration</option>
                                        </optgroup>
                                        <optgroup label="— Selection —">
                                            <option value="Drop-Down">Drop-Down (Single)</option>
                                            <option value="Multi-Select">Multi-Select</option>
                                            <option value="Radio">Radio Button</option>
                                            <option value="Checkbox">Checkbox (Boolean)</option>
                                            <option value="Toggle">Toggle</option>
                                        </optgroup>
                                        <optgroup label="— Lookup —">
                                            <option value="User Picker">User Picker</option>
                                            <option value="File Attachment">File Attachment</option>
                                        </optgroup>
                                    </select>
                                </div>
                                {/* Dropdown Options — shown when field type needs values */}
                                {(row.field_type === 'Drop-Down' || row.field_type === 'Multi-Select') && row.is_selected && (
                                    <div className="w-full mt-2 px-3 pl-[52px]">
                                        <input
                                            type="text"
                                            value={row.options || ''}
                                            onChange={e => handleGridRowChange(idx, 'options', e.target.value)}
                                            placeholder="Enter options separated by commas (e.g. Open, Closed, Pending)"
                                            className="w-full bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 text-xs text-gray-700 dark:text-slate-300 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-400 outline-none placeholder:text-gray-400"
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        )}
        {/* ── System Columns Configuration ── */}
        {(() => {
            const SYSTEM_COLS = [
                { key: 'status',      icon: '🔄', label: 'Status',      desc: 'Platform-managed workflow status' },
                { key: 'assignee',    icon: '📋', label: 'Assignee',    desc: 'Platform-managed assignment' },
                { key: 'modified_by', icon: '👤', label: 'Modified By', desc: 'Last user who edited' },
                { key: 'remarks',     icon: '💬', label: 'Remarks',     desc: 'Audit trail comments' },
                { key: 'modified_at', icon: '🕐', label: 'Modified At', desc: 'Last edit timestamp' },
            ];
            const enabled = form.workflow_config?.system_columns || [];
            const toggle = (key) => {
                const next = enabled.includes(key) ? enabled.filter(k => k !== key) : [...enabled, key];
                patchWf('system_columns', next);
                if (key === 'assignee' && enabled.includes('assignee')) {
                    patch('record_visibility', 'all');
                }
            };
            const customCols = form.workflow_config?.custom_columns || [];
            const TYPES = [
                { v: 'text', label: 'Text' }, { v: 'dropdown', label: 'Dropdown' },
                { v: 'number', label: 'Number' }, { v: 'date', label: 'Date' }, { v: 'checkbox', label: 'Checkbox' },
            ];
            const addCol = (label, type) => {
                const key = '__platform_custom_' + label.toLowerCase().replace(/[^a-z0-9]+/g, '_') + '__';
                if (customCols.some(c => c.key === key)) return;
                patchWf('custom_columns', [...customCols, { key, label, type, options: '' }]);
            };
            const removeCol = (key) => patchWf('custom_columns', customCols.filter(c => c.key !== key));
            const updateCol = (key, field, val) => patchWf('custom_columns', customCols.map(c => c.key === key ? { ...c, [field]: val } : c));
            const hasStatusCol = enabled.includes('status');

            return (
                <div className="space-y-5 border-t border-gray-200 dark:border-slate-700 pt-4 mt-6">
                    {/* Section Header */}
                    <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-lg bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center">
                            <span className="text-xs">⚙️</span>
                        </div>
                        <div>
                            <p className="text-xs font-bold text-gray-700 dark:text-slate-200 uppercase tracking-wide">System Columns</p>
                            <p className="text-[10px] text-gray-400 dark:text-slate-500">Platform-managed columns & workflow settings — not part of your source data</p>
                        </div>
                    </div>

                    {/* ── Column Toggles ── */}
                    <div className="space-y-2">
                        <p className="text-[10px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">Column Visibility</p>
                        <div className="grid grid-cols-2 gap-2">
                            {SYSTEM_COLS.map(sc => {
                                const isOn = enabled.includes(sc.key);
                                return (
                                    <button key={sc.key} type="button" onClick={() => toggle(sc.key)}
                                        className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-2xl border text-left transition-all ${
                                            isOn
                                                ? 'bg-indigo-50 dark:bg-indigo-950/30 border-indigo-200 dark:border-indigo-800/50'
                                                : 'bg-gray-50/50 dark:bg-slate-800/30 border-gray-200 dark:border-slate-700/60 opacity-50 hover:opacity-80'
                                        }`}>
                                        <span className="text-sm">{sc.icon}</span>
                                        <div className="flex-1 min-w-0">
                                            <p className={`text-[11px] font-bold truncate ${isOn ? 'text-indigo-700 dark:text-indigo-300' : 'text-gray-500 dark:text-slate-400'}`}>{sc.label}</p>
                                            <p className="text-[9px] text-gray-400 dark:text-slate-500 truncate">{sc.desc}</p>
                                        </div>
                                        <div className={`w-4 h-4 rounded-md border-2 flex items-center justify-center transition-all ${
                                            isOn ? 'bg-indigo-500 border-indigo-500' : 'border-gray-300 dark:border-slate-600'
                                        }`}>
                                            {isOn && <span className="text-white text-[8px] font-black">✓</span>}
                                        </div>
                                    </button>
                                );
                            })}
                            {customCols.map(col => (
                                <div key={col.key} className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-2xl border border-amber-200 dark:border-amber-800/40 bg-amber-50/50 dark:bg-amber-950/20">
                                    <span className="text-sm">📝</span>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-[11px] font-bold text-amber-700 dark:text-amber-300 truncate">{col.label}</p>
                                        <div className="flex items-center gap-1.5 mt-0.5">
                                            <select value={col.type} onChange={e => updateCol(col.key, 'type', e.target.value)}
                                                className="text-[9px] px-1.5 py-0.5 rounded-md border border-amber-200 dark:border-amber-700 bg-white dark:bg-slate-900 dark:text-slate-200 outline-none">
                                                {TYPES.map(t => <option key={t.v} value={t.v}>{t.label}</option>)}
                                            </select>
                                            {col.type === 'dropdown' && (
                                                <input type="text" value={col.options || ''} onChange={e => updateCol(col.key, 'options', e.target.value)}
                                                    placeholder="opt1, opt2"
                                                    className="text-[9px] px-1.5 py-0.5 rounded-md border border-amber-200 dark:border-amber-700 bg-white dark:bg-slate-900 dark:text-slate-200 outline-none w-20" />
                                            )}
                                        </div>
                                    </div>
                                    <button type="button" onClick={() => removeCol(col.key)}
                                        className="text-amber-400 hover:text-red-500 transition-colors text-[10px] font-black px-1">✕</button>
                                </div>
                            ))}
                            {/* ── Add custom column — rendered as next cell in grid ── */}
                            <div className="flex items-center gap-2 px-3.5 py-2.5 rounded-2xl border border-dashed border-gray-300 dark:border-slate-600 bg-gray-50/30 dark:bg-slate-800/20 hover:border-amber-300 dark:hover:border-amber-700 transition-colors">
                                <span className="text-sm opacity-40">➕</span>
                                <input type="text" placeholder="Column name…" id="__custom_col_name_input"
                                    className="flex-1 text-xs bg-transparent outline-none text-gray-700 dark:text-slate-200 placeholder:text-gray-400 dark:placeholder:text-slate-500"
                                    onKeyDown={e => {
                                        if (e.key === 'Enter') { e.preventDefault(); const val = e.target.value.trim(); if (val) { addCol(val, 'text'); e.target.value = ''; } }
                                    }} />
                                <button type="button"
                                    onClick={() => { const el = document.getElementById('__custom_col_name_input'); const val = el?.value?.trim(); if (val) { addCol(val, 'text'); el.value = ''; } }}
                                    className="px-2.5 py-1 rounded-lg text-[10px] font-bold text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/40 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-all whitespace-nowrap">
                                    + Add
                                </button>
                            </div>
                        </div>
                        <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-1">
                            Custom columns are platform-managed — <strong>never</strong> synced to source.
                        </p>
                    </div>

                    {/* ── Workflow Statuses ── */}
                    <div className="space-y-2">
                        <p className="text-[10px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">Workflow Statuses</p>
                        <p className="text-[10px] mb-1">
                            {hasStatusCol
                                ? <span className="text-emerald-600 dark:text-emerald-400 font-semibold">✓ Status column mapped — add or remove status values below</span>
                                : <span className="text-amber-500 font-semibold">⚠ Tip: map a column to <strong>Status</strong> in Core Mapping to drive these from your data</span>
                            }
                        </p>
                        <div className={`flex flex-wrap gap-2 p-3 rounded-2xl border ${errors.statuses ? 'border-red-400 dark:border-red-500' : 'border-gray-200 dark:border-slate-700/80'} bg-gray-50/50 dark:bg-slate-800/30 min-h-[44px]`}>
                            {(form.workflow_config?.statuses || []).map((s, i) => (
                                <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-xl text-xs font-bold border border-indigo-200 dark:border-indigo-800/50">
                                    <span className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
                                    {s}
                                    <button type="button" onClick={() => {
                                        const next = (form.workflow_config?.statuses || []).filter((_, idx) => idx !== i);
                                        patchWf('statuses', next);
                                    }} className="ml-0.5 text-indigo-400 hover:text-red-500 transition-colors text-[10px] font-black">✕</button>
                                </span>
                            ))}
                            <input type="text" placeholder="Type & press Enter to add a status…"
                                className="flex-1 min-w-[160px] bg-transparent text-xs text-gray-700 dark:text-slate-300 outline-none placeholder:text-gray-400"
                                onKeyDown={e => {
                                    if (e.key === 'Enter' && e.target.value.trim()) {
                                        e.preventDefault();
                                        const val = e.target.value.trim();
                                        const current = form.workflow_config?.statuses || [];
                                        if (!current.includes(val)) patchWf('statuses', [...current, val]);
                                        e.target.value = '';
                                        if (errors.statuses) setErrors(prev => ({ ...prev, statuses: undefined }));
                                    }
                                }}
                            />
                        </div>
                        {errors.statuses && <p className="text-[10px] text-red-500 font-semibold mt-1">{errors.statuses}</p>}
                    </div>
                </div>
            );
        })()}



    </StepCard>
)}


                {step === 3 && (
                    <StepCard title="Access & Write-back" desc="Configure who can access this worklist, what they can see, and how edits are written back.">

                        {/* ── SECTION: Role Access ── */}
                        <div className="space-y-4">
                            <div className="flex items-center gap-2 pb-1 border-b border-gray-100 dark:border-slate-800">
                                <div className="w-5 h-5 rounded-lg bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center">
                                    <span className="text-[10px]">👥</span>
                                </div>
                                <p className="text-xs font-bold text-gray-700 dark:text-slate-200 uppercase tracking-wide">Role Access</p>
                            </div>

                            <Field label="Roles with Access">
                                <p className="text-[10px] text-gray-400 dark:text-slate-500 mb-2">Select which roles can view and interact with this worklist. Leave all unselected to allow all roles.</p>
                                <div className="flex flex-wrap gap-2">
                                    {visibleRoles.map(r => {
                                        const isSelected = form.shared_with?.includes(r.id) && !form.shared_with?.includes('*');
                                        return (
                                            <button key={r.id} type="button" onClick={() => {
                                                let next = [...(form.shared_with || [])].filter(x => x !== '*');
                                                if (isSelected) {
                                                    next = next.filter(x => x !== r.id);
                                                } else {
                                                    next.push(r.id);
                                                }
                                                patch('shared_with', next.length === 0 ? ['*'] : next);
                                            }}
                                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border-2 transition-all ${
                                                isSelected
                                                    ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                                                    : 'border-gray-200 dark:border-slate-700 text-gray-500 dark:text-slate-400 hover:border-indigo-300 dark:hover:border-indigo-800 bg-white dark:bg-slate-900'
                                            }`}>
                                                {isSelected && <Check className="w-3 h-3" />}
                                                {r.name || r.id}
                                            </button>
                                        );
                                    })}
                                    {visibleRoles.length === 0 && <p className="text-xs text-gray-400">No roles available</p>}
                                </div>
                                {form.shared_with?.includes('*') && (
                                    <p className="mt-2 text-[10px] text-amber-600 dark:text-amber-400 font-semibold">⚠ No roles selected — all users can access this worklist</p>
                                )}
                            </Field>

                            <UserPillSelect
                                label="Include Specific Users"
                                hint="Grant access to specific users regardless of their role."
                                users={users}
                                selectedIds={form.include_users || []}
                                onAdd={id => patch('include_users', [...(form.include_users || []), id])}
                                onRemove={id => patch('include_users', (form.include_users || []).filter(x => x !== id))}
                                searchVal={userSearchInclude}
                                onSearch={setUserSearchInclude}
                                excludeIds={form.exclude_users || []}
                            />

                            <UserPillSelect
                                label="Exclude Specific Users"
                                hint="Explicitly deny access to these users, even if they are in an allowed role."
                                users={users}
                                selectedIds={form.exclude_users || []}
                                onAdd={id => patch('exclude_users', [...(form.exclude_users || []), id])}
                                onRemove={id => patch('exclude_users', (form.exclude_users || []).filter(x => x !== id))}
                                searchVal={userSearchExclude}
                                onSearch={setUserSearchExclude}
                                excludeIds={form.include_users || []}
                            />
                        </div>

                        {/* ── SECTION: Record Visibility ── */}
                        <div className="space-y-4 pt-2">
                            <div className="flex items-center gap-2 pb-1 border-b border-gray-100 dark:border-slate-800">
                                <div className="w-5 h-5 rounded-lg bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center">
                                    <span className="text-[10px]">👁</span>
                                </div>
                                <p className="text-xs font-bold text-gray-700 dark:text-slate-200 uppercase tracking-wide">Record Visibility</p>
                            </div>
                            <Field label="What records can users see?">
                                <div className="grid grid-cols-2 gap-3">
                                    {[
                                        { v: 'all', label: 'View All Records', desc: 'Users can see every record in this worklist regardless of assignment', icon: '🌐', disabled: false },
                                        { v: 'assigned', label: 'Assigned Only', desc: hasAssigneeCol ? 'Users can only see records that are assigned to them' : 'Requires Assignee system column to be enabled', icon: '🎯', disabled: !hasAssigneeCol },
                                    ].map(opt => {
                                        const isDisabled = opt.disabled;
                                        return (
                                            <button key={opt.v} type="button"
                                                onClick={() => !isDisabled && patch('record_visibility', opt.v)}
                                                disabled={isDisabled}
                                                className={`text-left p-4 rounded-2xl border-2 transition-all ${
                                                    form.record_visibility === opt.v
                                                        ? 'border-emerald-500 bg-emerald-50/50 dark:bg-emerald-900/20'
                                                        : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600 bg-white dark:bg-slate-900'
                                                } ${isDisabled ? 'opacity-40 cursor-not-allowed border-dashed' : ''}`}>
                                                <div className="flex items-center justify-between mb-1.5">
                                                    <span className="text-base">{opt.icon}</span>
                                                    {!isDisabled && (
                                                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${form.record_visibility === opt.v ? 'border-emerald-500 bg-emerald-500' : 'border-gray-300 dark:border-slate-600'}`}>
                                                            {form.record_visibility === opt.v && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                                                        </div>
                                                    )}
                                                </div>
                                                <p className="text-xs font-bold text-gray-800 dark:text-slate-200">{opt.label}</p>
                                                <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-0.5 leading-relaxed">{opt.desc}</p>
                                            </button>
                                        );
                                    })}
                                </div>
                            </Field>
                        </div>

                        {/* ── SECTION: New Record Entry ── */}
                        <div className="space-y-4 pt-2">
                            <div className="flex items-center gap-2 pb-1 border-b border-gray-100 dark:border-slate-800">
                                <div className="w-5 h-5 rounded-lg bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center">
                                    <span className="text-[10px]">➕</span>
                                </div>
                                <p className="text-xs font-bold text-gray-700 dark:text-slate-200 uppercase tracking-wide">New Record Entry</p>
                                <p className="text-[10px] text-gray-400 dark:text-slate-500 ml-auto">Can users add new rows to this worklist?</p>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                {[
                                    { v: 'all',  icon: '✅', label: 'Enabled',  desc: 'Users with access can add new records via the form' },
                                    { v: 'none', icon: '🚫', label: 'Disabled', desc: 'No one can add new records — data comes from the source only' },
                                ].map(opt => (
                                    <button key={opt.v} type="button" onClick={() => patch('allow_add_entry', opt.v)}
                                        className={`text-left p-3 rounded-2xl border-2 transition-all ${
                                            (form.allow_add_entry || 'all') === opt.v
                                                ? 'border-emerald-500 bg-emerald-50/50 dark:bg-emerald-900/20'
                                                : 'border-gray-200 dark:border-slate-700 hover:border-emerald-300 bg-white dark:bg-slate-900'
                                        }`}>
                                        <p className="text-lg mb-1">{opt.icon}</p>
                                        <p className="text-xs font-bold text-gray-800 dark:text-slate-200">{opt.label}</p>
                                        <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-0.5 leading-relaxed">{opt.desc}</p>
                                    </button>
                                ))}
                            </div>
                            {(form.allow_add_entry || 'all') !== 'none' && (
                                <div className="mt-2 p-3 bg-gray-50 dark:bg-slate-800/50 rounded-xl">
                                    <p className="text-[10px] text-gray-500 dark:text-slate-400 font-semibold">
                                        ℹ️ The Add Entry form will show all <strong>editable</strong> fields defined in Step 2. Read-only fields are excluded automatically.
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* ── SECTION: Pull Data Permission ── */}
                        <div className="space-y-4 pt-2">
                            <div className="flex items-center gap-2 pb-1 border-b border-gray-100 dark:border-slate-800">
                                <div className="w-5 h-5 rounded-lg bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center">
                                    <span className="text-[10px]">📡</span>
                                </div>
                                <p className="text-xs font-bold text-gray-700 dark:text-slate-200 uppercase tracking-wide">Pull Data Permission</p>
                                <p className="text-[10px] text-gray-400 dark:text-slate-500 ml-auto">Can users trigger a data pull from the source?</p>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                {[
                                    { v: 'all',  icon: '✅', label: 'Enabled',  desc: 'Users with access can pull fresh data from the source' },
                                    { v: 'none', icon: '🚫', label: 'Disabled', desc: 'Pull is disabled — data is only refreshed via scheduled sync' },
                                ].map(opt => (
                                    <button key={opt.v} type="button" onClick={() => patch('allow_pull', opt.v)}
                                        className={`text-left p-3 rounded-2xl border-2 transition-all ${
                                            (form.allow_pull || 'all') === opt.v
                                                ? 'border-blue-500 bg-blue-50/50 dark:bg-blue-900/20'
                                                : 'border-gray-200 dark:border-slate-700 hover:border-blue-300 bg-white dark:bg-slate-900'
                                        }`}>
                                        <p className="text-lg mb-1">{opt.icon}</p>
                                        <p className="text-xs font-bold text-gray-800 dark:text-slate-200">{opt.label}</p>
                                        <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-0.5 leading-relaxed">{opt.desc}</p>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* ── SECTION: Assignee Configuration ── */}
                        {hasAssigneeCol && (
                            <div className="space-y-4 pt-2">
                                <div className="flex items-center gap-2 pb-1 border-b border-gray-100 dark:border-slate-800">
                                    <div className="w-5 h-5 rounded-lg bg-violet-100 dark:bg-violet-900/40 flex items-center justify-center">
                                        <span className="text-[10px]">👤</span>
                                    </div>
                                    <p className="text-xs font-bold text-gray-700 dark:text-slate-200 uppercase tracking-wide">Assignee Configuration</p>
                                </div>
                                <p className="text-[10px] text-emerald-600 dark:text-emerald-400 font-semibold">
                                    📋 Assignee is a platform-managed column — it appears in the worklist runner but is <strong>never written back</strong> to your source data.
                                </p>
                                <UserPillSelect
                                    label="Assignee Pool"
                                    hint="Only users in this pool can be assigned work items. Leave empty to allow any user with access."
                                    users={users}
                                    selectedIds={form.workflow_config?.assignee_pool || []}
                                    onAdd={id => patchWf('assignee_pool', [...(form.workflow_config?.assignee_pool || []), id])}
                                    onRemove={id => patchWf('assignee_pool', (form.workflow_config?.assignee_pool || []).filter(x => x !== id))}
                                    searchVal={userSearchAssignee}
                                    onSearch={setUserSearchAssignee}
                                />

                                <Field label="Default Assignee">
                                    <p className="text-[10px] text-gray-400 dark:text-slate-500 mb-2">New items will be auto-assigned to this user. Select None to leave unassigned.</p>
                                    <select className={input} value={form.default_assignee || ''} onChange={e => patch('default_assignee', e.target.value)}>
                                        <option value="">None (Unassigned)</option>
                                        {(form.workflow_config?.assignee_pool?.length > 0
                                            ? users.filter(u => form.workflow_config.assignee_pool.includes(u.id))
                                            : users
                                        ).map(u => (
                                            <option key={u.id} value={u.id}>{u.name || u.email}</option>
                                        ))}
                                    </select>
                                </Field>
                            </div>
                        )}


                        {/* ── SECTION: Locked Statuses ── */}
                        <div className="space-y-4 pt-2">
                            <div className="flex items-center gap-2 pb-1 border-b border-gray-100 dark:border-slate-800">
                                <div className="w-5 h-5 rounded-lg bg-red-100 dark:bg-red-900/40 flex items-center justify-center">
                                    <span className="text-[10px]">🔒</span>
                                </div>
                                <p className="text-xs font-bold text-gray-700 dark:text-slate-200 uppercase tracking-wide">Locked Statuses</p>
                            </div>
                            <Field label="Record Lock">
                                <p className="text-[10px] text-gray-400 dark:text-slate-500 mb-3">Items in these statuses cannot be edited by regular users. Admins can still override.</p>
                                <div className="flex flex-wrap gap-2">
                                    {(form.workflow_config?.statuses?.length > 0
                                        ? form.workflow_config.statuses
                                        : ['Open', 'In Progress', 'Pending Review', 'Closed']
                                    ).map(s => {
                                        const terminalStatuses = form.workflow_config?.terminal_statuses || ['Closed'];
                                        const isLocked = terminalStatuses.includes(s);
                                        return (
                                            <button key={s} type="button"
                                                onClick={() => {
                                                    const current = form.workflow_config?.terminal_statuses || ['Closed'];
                                                    const next = isLocked ? current.filter(x => x !== s) : [...current, s];
                                                    patch('workflow_config', { ...(form.workflow_config || {}), terminal_statuses: next });
                                                }}
                                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold border-2 transition-all ${
                                                    isLocked
                                                        ? 'border-red-400 bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-400'
                                                        : 'border-gray-200 dark:border-slate-700 text-gray-500 dark:text-slate-400 hover:border-red-300 dark:hover:border-red-800 bg-white dark:bg-slate-900'
                                                }`}>
                                                {isLocked ? '🔒' : '🔓'} {s}
                                            </button>
                                        );
                                    })}
                                </div>
                                {(form.workflow_config?.terminal_statuses?.length > 0) && (
                                    <p className="mt-2 text-[10px] text-red-500 dark:text-red-400 font-semibold">
                                        🔒 {form.workflow_config.terminal_statuses.join(', ')} — locked for regular users
                                    </p>
                                )}
                                {!(form.workflow_config?.system_columns || []).includes('status') && (
                                    <p className="mt-1 text-[10px] text-amber-500 font-semibold">⚠ Status system column not enabled — enable it in Step 2 → System Columns first</p>
                                )}
                            </Field>
                        </div>

                        {/* ── SECTION: Default View ── */}
                        <div className="space-y-4 pt-2">
                            <div className="flex items-center gap-2 pb-1 border-b border-gray-100 dark:border-slate-800">
                                <div className="w-5 h-5 rounded-lg bg-violet-100 dark:bg-violet-900/40 flex items-center justify-center">
                                    <span className="text-[10px]">🔀</span>
                                </div>
                                <p className="text-xs font-bold text-gray-700 dark:text-slate-200 uppercase tracking-wide">Default View</p>
                                <p className="text-[10px] text-gray-400 dark:text-slate-500 ml-auto">Applied when users open this worklist</p>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <Field label="Default Sort Column">
                                    <select
                                        value={form.default_sort_col || ''}
                                        onChange={e => patch('default_sort_col', e.target.value)}
                                        className={input}>
                                        <option value="">— None (server order) —</option>
                                        {(form.schema_map || []).filter(r => r.is_selected !== false && r.source_field && !r.source_field.endsWith(' [Removed]')).map(r => (
                                            <option key={r.source_field} value={r.source_field}>{r.source_field}</option>
                                        ))}
                                    </select>
                                </Field>
                                <Field label="Default Sort Direction">
                                    <select
                                        value={form.default_sort_dir || 'asc'}
                                        onChange={e => patch('default_sort_dir', e.target.value)}
                                        disabled={!form.default_sort_col}
                                        className={`${input} disabled:opacity-40`}>
                                        <option value="asc">↑ Ascending (A → Z, 0 → 9)</option>
                                        <option value="desc">↓ Descending (Z → A, 9 → 0)</option>
                                    </select>
                                </Field>
                            </div>
                            {form.default_sort_col && (
                                <p className="text-[10px] text-indigo-600 dark:text-indigo-400 font-semibold">
                                    ✓ Worklist opens sorted by <strong>{form.default_sort_col}</strong> {form.default_sort_dir === 'desc' ? 'descending ↓' : 'ascending ↑'} by default
                                </p>
                            )}
                        </div>

                    </StepCard>
                )}

                {/* ══ STEP 4 — WRITE-BACK ══════════════════════════════════════════ */}
                {step === 4 && (() => {
                    const editableFields = (form.schema_map || []).filter(r => r.is_selected !== false && !r.is_read_only);
                    const sink = form.writeback_sink || { type: 'same_as_source', write_mode: 'update_rows', target_type: 'table' };
                    const patchSink = (key, val) => setForm(f => ({ ...f, writeback_sink: { ...(f.writeback_sink || {}), [key]: val } }));

                    let sourceSummary = '';
                    if (form.source_type === 'sharepoint') {
                        const sp = form.sharepoint_config || {};
                        sourceSummary = `${sp.workbook_path || sp.workbook_file || 'workbook'} → ${sp.worksheet_name || 'Sheet1'}`;
                    } else if (form.source_type === 'data_model') {
                        const dm = dataModels.find(d => d.id === form.data_model_id);
                        sourceSummary = `${dm?.name || 'Data Model'} / ${form.data_model_table || 'table'} (underlying SQL)`;
                    } else {
                        sourceSummary = form.source_table || 'SQL source';
                    }

                    const sinkTypes = [
                        { id: 'same_as_source', icon: '↩️', label: 'Same as Source',      desc: sourceSummary },
                        { id: 'sql',            icon: '🗄️', label: 'Different SQL Table',  desc: 'Write to any SQL datasource + table' },
                        { id: 'sharepoint',     icon: '📋', label: 'Different SharePoint', desc: 'Write to another SharePoint workbook / sheet' },
                        { id: 'platform',       icon: '🔒', label: 'Platform-managed only', desc: 'Keep edits inside the platform — no external push' },
                    ];

                    // For file-based datasources, "Same as Source" doesn't apply
                    const isFileSource = selectedDsIsFile;
                    // For Data Model sources, "Same as Source" doesn't apply — Data Models are an
                    // abstraction layer; write-back should target a specific destination explicitly.
                    const isDmSource = form.source_type === 'data_model';
                    const hideSameAsSource = isFileSource || isDmSource;
                    const filteredSinkTypes = hideSameAsSource
                        ? sinkTypes.filter(s => s.id !== 'same_as_source')
                        : sinkTypes;

                    // Only 'scheduled' trigger is supported — runs automatically via background sweeper

                    return (
                        <StepCard title="Write-back" desc="Define WHERE edits go, WHAT fields are synced, and WHEN the sync happens.">

                            {/* ── Source write-back limitation notice ─────────────── */}
                            {hideSameAsSource && (
                                <div className="flex items-center gap-3 p-4 mb-4 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/30 rounded-xl">
                                    <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0" />
                                    <p className="text-sm text-amber-700 dark:text-amber-300">
                                        {isFileSource ? (
                                            <><strong>{selectedDs?.name}</strong> is a file-based datasource (CSV / Excel / JSON / Parquet). Direct write-back to the source file is not supported.</>
                                        ) : (
                                            <>Data Models are an abstraction layer over the source database. Please choose an explicit write-back target below.</>
                                        )}
                                        {' '}Choose a different write-back target below, or use <strong>Platform-managed</strong> to keep edits within the platform.
                                    </p>
                                </div>
                            )}

                            {/* ── A: Sink Target ──────────────────────────────────────── */}
                            <div>
                                <p className="text-xs font-extrabold text-gray-700 dark:text-slate-300 mb-3">
                                    A — Write-back Target <span className="font-normal text-gray-400">(WHERE edits go)</span>
                                </p>
                                <div className="grid grid-cols-1 gap-2">
                                    {filteredSinkTypes.map(s => (
                                        <button key={s.id} type="button" onClick={() => patchSink('type', s.id)}
                                            className={`text-left px-4 py-3 rounded-2xl border-2 transition-all flex items-center gap-3 ${
                                                sink.type === s.id
                                                    ? 'border-indigo-500 bg-indigo-50/50 dark:bg-indigo-900/20'
                                                    : 'border-gray-200 dark:border-slate-700 hover:border-indigo-300 bg-white dark:bg-slate-900'
                                            }`}>
                                            <span className="text-xl shrink-0">{s.icon}</span>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs font-bold text-gray-800 dark:text-slate-200">{s.label}</p>
                                                <p className="text-[10px] text-gray-400 dark:text-slate-500 truncate">{s.desc}</p>
                                            </div>
                                            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                                                sink.type === s.id ? 'border-indigo-500 bg-indigo-500' : 'border-gray-300 dark:border-slate-600'
                                            }`}>
                                                {sink.type === s.id && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                                            </div>
                                        </button>
                                    ))}
                                </div>



                                {/* SQL sink sub-config */}
                                {sink.type === 'sql' && (
                                    <div className="mt-3 p-4 bg-gray-50 dark:bg-slate-800/50 rounded-2xl space-y-3">
                                        <p className="text-[10px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-widest">SQL Target</p>
                                        <Field label="Target Datasource">
                                            <select className={input} value={sink.datasource_id || ''} onChange={e => patchSink('datasource_id', e.target.value)}>
                                                <option value="">— select datasource —</option>
                                                {datasources.filter(ds => !FILE_DS_TYPES.has(ds.type)).map(ds => <option key={ds.id} value={ds.id}>{ds.name}</option>)}
                                            </select>
                                        </Field>
                                        <Field label="Target Table">
                                            <input className={input} placeholder="e.g. dbo.WorklistResults" value={sink.table || ''} onChange={e => patchSink('table', e.target.value)} />
                                        </Field>

                                    </div>
                                )}

                                {/* SharePoint different sink sub-config */}
                                {sink.type === 'sharepoint' && (
                                    <div className="mt-3 p-4 bg-gray-50 dark:bg-slate-800/50 rounded-2xl space-y-3">
                                        <p className="text-[10px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-widest">Target SharePoint</p>
                                        
                                        <Field label="SharePoint Domain *">
                                            <div className="flex items-center gap-2">
                                                <input className={input} placeholder="aidastech0" value={sink.site_hostname || ''} onChange={e => patchSink('site_hostname', e.target.value)} />
                                                <span className="text-sm text-gray-400 dark:text-slate-500 whitespace-nowrap">.sharepoint.com</span>
                                            </div>
                                        </Field>
                                        
                                        <Field label="Site Name *">
                                            <input className={input} placeholder="AIDASPlatform" value={sink.site_path || ''} onChange={e => patchSink('site_path', e.target.value)} />
                                        </Field>
                                        
                                        <Field label="SharePoint Folder Path">
                                            <input className={input} placeholder="aidas_apps_demo/patients_charts" value={sink.workbook_path || ''} onChange={e => patchSink('workbook_path', e.target.value)} />
                                            <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">Folder path INSIDE the document library (e.g., /GenAI/Internal). Do NOT include the library name.</p>
                                        </Field>
                                        
                                        <Field label="Workbook File Name *">
                                            <input className={input} placeholder="ARClaims.xlsx" value={sink.workbook_file || ''} onChange={e => patchSink('workbook_file', e.target.value)} />
                                        </Field>
                                        
                                        <Field label="Worksheet Name">
                                            <input className={input} placeholder="Sheet1" value={sink.worksheet_name || ''} onChange={e => patchSink('worksheet_name', e.target.value)} />
                                        </Field>
                                        {/* Azure AD Credentials */}
                                        <div className="mt-2 pt-3 border-t border-gray-200 dark:border-slate-700">
                                            <div className="flex items-center justify-between mb-2">
                                                <p className="text-[10px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-widest">Azure AD Credentials</p>
                                                <button type="button" onClick={() => patchSink('use_integration_creds', !sink.use_integration_creds)}
                                                    className="text-[10px] font-semibold text-indigo-600 dark:text-indigo-400 hover:underline">
                                                    {sink.use_integration_creds !== false ? '🔗 Using Integration Credentials' : '🔑 Using Custom Credentials'}
                                                </button>
                                            </div>
                                            {sink.use_integration_creds === false ? (
                                                <div className="space-y-2">
                                                    <Field label="Azure Tenant ID">
                                                        <input className={input} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" value={sink.sp_tenant_id || ''} onChange={e => patchSink('sp_tenant_id', e.target.value)} />
                                                    </Field>
                                                    <Field label="Client ID">
                                                        <input className={input} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" value={sink.sp_client_id || ''} onChange={e => patchSink('sp_client_id', e.target.value)} />
                                                    </Field>
                                                    <Field label="Client Secret">
                                                        <input type="password" className={input} placeholder="••••••••••••••••••••" value={sink.sp_client_secret || ''} onChange={e => patchSink('sp_client_secret', e.target.value)} />
                                                    </Field>
                                                </div>
                                            ) : (
                                                <p className="text-[10px] text-gray-400 dark:text-slate-500 bg-gray-100 dark:bg-slate-800 px-3 py-2 rounded-xl">
                                                    ✅ Uses the same Azure AD credentials from your SharePoint integration. Click above to enter custom credentials.
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                )}


                                {/* Platform-only info */}
                                {sink.type === 'platform' && (
                                    <div className="mt-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-2xl border border-blue-200 dark:border-blue-800/40">
                                        <p className="text-[11px] text-blue-700 dark:text-blue-300 font-semibold">
                                            🔒 <strong>Platform-managed only</strong> — user edits are stored inside the platform only. No data is pushed to any external system. The "Sync Changes" button will be hidden from users.
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* ── B: Fields to Write Back ──────────────────────────────── */}
                            {(sink.type === 'sql' || sink.type === 'sharepoint') && (
                                <div className="pt-5 border-t border-gray-100 dark:border-slate-800">
                                    <button type="button" onClick={() => setForm(f => ({ ...f, _wbFieldsOpen: !f._wbFieldsOpen }))}
                                        className="w-full flex items-center justify-between mb-1 group">
                                        <div>
                                            <p className="text-xs font-extrabold text-gray-700 dark:text-slate-300">
                                                B — Fields to Write Back <span className="font-normal text-gray-400">(WHAT is synced)</span>
                                                {editableFields.length > 0 && (
                                                    <span className="ml-2 text-[10px] font-semibold text-indigo-500 dark:text-indigo-400">
                                                        {form.writeback_fields ? form.writeback_fields.length : editableFields.length}/{editableFields.length} selected
                                                    </span>
                                                )}
                                            </p>
                                            <p className="text-[10px] text-gray-400 dark:text-slate-500">Read-only fields are always excluded. Un-tick to exclude an editable field from sync.</p>
                                        </div>
                                        <span className={`text-gray-400 text-xs transition-transform ${form._wbFieldsOpen ? 'rotate-180' : ''}`}>▼</span>
                                    </button>
                                    {form._wbFieldsOpen && (
                                        editableFields.length === 0 ? (
                                            <p className="text-[11px] text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 rounded-xl mt-2">⚠️ No editable fields — go back to Step 2 and mark some fields as Editable.</p>
                                        ) : (
                                            <div className="space-y-1 mt-2 max-h-[300px] overflow-y-auto rounded-xl border border-gray-100 dark:border-slate-800 p-1">
                                                {editableFields.map(r => {
                                                    const allNames = editableFields.map(f => f.source_field);
                                                    const included = !form.writeback_fields || form.writeback_fields.includes(r.source_field);
                                                    return (
                                                        <label key={r.source_field} className="flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-800/50 cursor-pointer transition-colors">
                                                            <input type="checkbox" checked={included} className="w-4 h-4 rounded accent-indigo-600"
                                                                onChange={e => {
                                                                    const base = form.writeback_fields || allNames;
                                                                    const next = e.target.checked
                                                                        ? [...new Set([...base, r.source_field])]
                                                                        : base.filter(f => f !== r.source_field);
                                                                    setForm(f => ({ ...f, writeback_fields: next }));
                                                                }} />
                                                            <div>
                                                                <p className="text-xs font-semibold text-gray-800 dark:text-slate-200">{r.source_field}</p>
                                                                <p className="text-[10px] text-gray-400">{r.field_type || 'Text Box'}{r.core_mapping && r.core_mapping !== 'None' ? ` · ${r.core_mapping}` : ''}</p>
                                                            </div>
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        )
                                    )}
                                </div>
                            )}

                            {/* ── C: Auto-Sync Toggle ──────────────────────────────────── */}
                            <div className="pt-5 border-t border-gray-100 dark:border-slate-800">
                                <p className="text-xs font-extrabold text-gray-700 dark:text-slate-300 mb-3">
                                    C — Auto-Sync <span className="font-normal text-gray-400">(background write-back)</span>
                                </p>
                                <div className={`flex items-center justify-between p-4 rounded-2xl border-2 transition-all ${
                                    form.writeback_trigger === 'scheduled'
                                        ? 'border-indigo-500 bg-indigo-50/50 dark:bg-indigo-900/20'
                                        : 'border-gray-200 dark:border-slate-700 bg-gray-50/50 dark:bg-slate-800/30'
                                }`}>
                                    <div className="flex items-center gap-3">
                                        <Clock className={`w-4 h-4 flex-shrink-0 transition-colors ${
                                            form.writeback_trigger === 'scheduled' ? 'text-indigo-500' : 'text-gray-400'
                                        }`} />
                                        <div>
                                            <p className="text-xs font-bold text-gray-800 dark:text-slate-200">Scheduled Auto-Sync</p>
                                            <p className="text-[10px] text-gray-400 dark:text-slate-500">
                                                {form.writeback_trigger === 'scheduled'
                                                    ? 'Pending edits are automatically synced every 10 minutes. You can also click "Sync Changes" for an immediate sync.'
                                                    : 'Auto-sync is off. Users must click "Sync Changes" in the Runner to write back edits.'}
                                            </p>
                                        </div>
                                    </div>
                                    <button type="button"
                                        onClick={() => patch('writeback_trigger', form.writeback_trigger === 'scheduled' ? 'manual' : 'scheduled')}
                                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 ${
                                            form.writeback_trigger === 'scheduled' ? 'bg-indigo-600' : 'bg-gray-300 dark:bg-slate-600'
                                        }`}>
                                        <span className={`inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
                                            form.writeback_trigger === 'scheduled' ? 'translate-x-6' : 'translate-x-1'
                                        }`} />
                                    </button>
                                </div>
                                {form.writeback_trigger === 'scheduled' && (() => {
                                    // Parse current cron to determine active tab & values
                                    const cron = form.writeback_schedule || '';
                                    // Determine schedule tab from cron value
                                    const recurringPresets = {
                                        '*/10 * * * *': '10min',
                                        '*/30 * * * *': '30min',
                                        '0 * * * *': 'hourly',
                                        '0 */6 * * *': '6h',
                                        '0 0 * * *': 'daily',
                                        '0 8 * * 1-5': 'weekdays',
                                    };
                                    const presetKey = recurringPresets[cron] || null;

                                    // Determine active tab
                                    const schedTab = form._schedTab || (presetKey ? 'recurring' : cron ? 'custom' : 'interval');

                                    const setSchedTab = (tab) => setForm(f => ({ ...f, _schedTab: tab }));

                                    // Parse interval from cron if possible
                                    const parseInterval = () => {
                                        const m1 = cron.match(/^\*\/(\d+) \* \* \* \*$/);
                                        if (m1) return { value: parseInt(m1[1]), unit: 'Minutes' };
                                        const m2 = cron.match(/^0 \*\/(\d+) \* \* \*$/);
                                        if (m2) return { value: parseInt(m2[1]), unit: 'Hours' };
                                        return { value: form._schedIntervalVal || 10, unit: form._schedIntervalUnit || 'Minutes' };
                                    };
                                    const interval = parseInterval();

                                    const buildIntervalCron = (val, unit) => {
                                        const v = Math.max(1, parseInt(val) || 1);
                                        if (unit === 'Minutes') return `*/${v} * * * *`;
                                        if (unit === 'Hours') return `0 */${v} * * *`;
                                        if (unit === 'Days') return `0 0 */${v} * *`;
                                        return `*/${v} * * * *`;
                                    };

                                    const tabs = [
                                        { id: 'recurring', label: 'Recurring' },
                                        { id: 'interval', label: 'Interval' },
                                        { id: 'custom', label: 'Custom' },
                                    ];

                                    const presets = [
                                        { id: '10min',    label: 'Every 10 min',     cron: '*/10 * * * *', desc: 'High frequency — near real-time sync' },
                                        { id: '30min',    label: 'Every 30 min',     cron: '*/30 * * * *', desc: 'Balanced frequency' },
                                        { id: 'hourly',   label: 'Every hour',       cron: '0 * * * *',   desc: 'Standard — once per hour' },
                                        { id: '6h',       label: 'Every 6 hours',    cron: '0 */6 * * *', desc: 'Low frequency — 4× per day' },
                                        { id: 'daily',    label: 'Daily (midnight)', cron: '0 0 * * *',   desc: 'Once per day at 12:00 AM' },
                                        { id: 'weekdays', label: 'Weekdays 8 AM',    cron: '0 8 * * 1-5', desc: 'Business hours — Mon–Fri at 8 AM' },
                                    ];

                                    return (
                                        <div className="mt-3 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-2xl overflow-hidden">
                                            {/* Header */}
                                            <div className="px-4 pt-4 pb-0">
                                                <p className="text-xs font-bold text-gray-800 dark:text-slate-200 mb-3">Schedule Trigger</p>
                                                {/* Tab bar */}
                                                <div className="flex gap-0 border-b border-gray-200 dark:border-slate-700">
                                                    {tabs.map(t => (
                                                        <button key={t.id} type="button" onClick={() => setSchedTab(t.id)}
                                                            className={`px-4 py-2 text-xs font-semibold transition-all relative ${
                                                                schedTab === t.id
                                                                    ? 'text-indigo-600 dark:text-indigo-400'
                                                                    : 'text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300'
                                                            }`}>
                                                            {t.label}
                                                            {schedTab === t.id && (
                                                                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-2/3 h-0.5 bg-indigo-500 rounded-full" />
                                                            )}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Tab content */}
                                            <div className="p-4">
                                                {/* ── Recurring ── */}
                                                {schedTab === 'recurring' && (
                                                    <div className="grid grid-cols-2 gap-2">
                                                        {presets.map(p => (
                                                            <button key={p.id} type="button"
                                                                onClick={() => setForm(f => ({ ...f, writeback_schedule: p.cron }))}
                                                                className={`text-left px-3.5 py-3 rounded-xl border-2 transition-all ${
                                                                    cron === p.cron
                                                                        ? 'border-indigo-500 bg-indigo-50/60 dark:bg-indigo-900/25'
                                                                        : 'border-gray-150 dark:border-slate-700 hover:border-indigo-300 dark:hover:border-indigo-800 bg-gray-50/30 dark:bg-slate-800/30'
                                                                }`}>
                                                                <p className={`text-xs font-bold ${cron === p.cron ? 'text-indigo-700 dark:text-indigo-300' : 'text-gray-700 dark:text-slate-300'}`}>{p.label}</p>
                                                                <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-0.5">{p.desc}</p>
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}

                                                {/* ── Interval ── */}
                                                {schedTab === 'interval' && (
                                                    <div className="grid grid-cols-2 gap-3">
                                                        <Field label="Every">
                                                            <input type="number" min="1" max="999"
                                                                className={input}
                                                                value={form._schedIntervalVal ?? interval.value}
                                                                onChange={e => {
                                                                    const v = e.target.value;
                                                                    const unit = form._schedIntervalUnit || interval.unit;
                                                                    setForm(f => ({
                                                                        ...f,
                                                                        _schedIntervalVal: v,
                                                                        writeback_schedule: buildIntervalCron(v, unit),
                                                                    }));
                                                                }} />
                                                        </Field>
                                                        <Field label="Unit">
                                                            <select className={input}
                                                                value={form._schedIntervalUnit || interval.unit}
                                                                onChange={e => {
                                                                    const unit = e.target.value;
                                                                    const v = form._schedIntervalVal ?? interval.value;
                                                                    setForm(f => ({
                                                                        ...f,
                                                                        _schedIntervalUnit: unit,
                                                                        writeback_schedule: buildIntervalCron(v, unit),
                                                                    }));
                                                                }}>
                                                                <option value="Minutes">Minutes</option>
                                                                <option value="Hours">Hours</option>
                                                                <option value="Days">Days</option>
                                                            </select>
                                                        </Field>
                                                    </div>
                                                )}

                                                {/* ── Custom ── */}
                                                {schedTab === 'custom' && (
                                                    <div className="space-y-2">
                                                        <Field label="Cron Expression">
                                                            <input className={input} placeholder="*/10 * * * *"
                                                                value={cron}
                                                                onChange={e => setForm(f => ({ ...f, writeback_schedule: e.target.value }))} />
                                                        </Field>
                                                        <p className="text-[10px] text-gray-400 dark:text-slate-500">
                                                            Standard 5-field cron: <code className="bg-gray-100 dark:bg-slate-800 px-1.5 py-0.5 rounded text-[10px] font-mono">min hour day month weekday</code>
                                                        </p>
                                                        <div className="flex flex-wrap gap-1.5 mt-1">
                                                            {[
                                                                { l: '*/10 * * * *', d: 'Every 10 min' },
                                                                { l: '0 * * * *', d: 'Every hour' },
                                                                { l: '0 8 * * 1-5', d: 'Weekdays 8 AM' },
                                                                { l: '0 0 * * 0', d: 'Weekly Sun' },
                                                            ].map(ex => (
                                                                <button key={ex.l} type="button"
                                                                    onClick={() => setForm(f => ({ ...f, writeback_schedule: ex.l }))}
                                                                    className="text-[10px] px-2.5 py-1 rounded-lg border border-gray-200 dark:border-slate-700 text-gray-500 dark:text-slate-400 hover:border-indigo-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-all font-mono">
                                                                    {ex.l} <span className="font-sans text-gray-400 dark:text-slate-500">({ex.d})</span>
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Current cron summary */}
                                                {cron && (
                                                    <div className="mt-3 flex items-center gap-2 px-3 py-2 bg-indigo-50/60 dark:bg-indigo-900/15 rounded-xl border border-indigo-200/50 dark:border-indigo-800/30">
                                                        <Clock className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                                                        <p className="text-[10px] font-semibold text-indigo-700 dark:text-indigo-300">
                                                            Active schedule: <code className="bg-indigo-100 dark:bg-indigo-900/40 px-1.5 py-0.5 rounded font-mono text-[10px]">{cron}</code>
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>

                        </StepCard>
                    );
                })()}

                {/* ══ STEP 5 — PUBLISH ════════════════════════════════════════════ */}
                {step === 5 && (
                    <StepCard title="Publish" desc="Review your configuration, then publish this worklist to make it available to users.">

                        {/* ── Config summary ── */}

                        {(() => {
                            // is_selected defaults to true — never exclude a row unless explicitly deselected
                            const allCols  = form.schema_map || [];
                            const selectedCols = Array.isArray(allCols)
                                ? allCols.filter(r => r.is_selected !== false)
                                : [];
                            return (
                            <div className="space-y-3 bg-gray-50 dark:bg-slate-800/50 rounded-2xl p-4">
                                <p className="text-[10px] font-bold text-gray-400 dark:text-slate-500 uppercase tracking-widest mb-1">Configuration Summary</p>
                                <Row label="Name"       value={form.name} />
                                <Row label="Source"     value={
                                    form.source_type === 'data_model'
                                         ? `Data Model: ${dataModels.find(d => d.id === form.data_model_id)?.name || form.data_model_id} / ${form.data_model_table}`
                                         : form.source_type === 'sql' ? `SQL — ${form.source_table || 'Custom query'}` : 'SharePoint'
                                } />
                                <Row label="Statuses"   value={(form.workflow_config?.statuses || []).join(' → ') || '—'} />
                                <Row label="Access"     value={
                                    !form.shared_with || form.shared_with.includes('*')
                                        ? 'All users'
                                        : form.shared_with.map(id => roles.find(r => r.id === id)?.name || id).join(', ')
                                } />
                                <Row label="Visibility"  value={form.record_visibility === 'assigned' ? 'Assigned items only' : 'All records'} />
                                <Row label="Write-back Target" value={
                                    form.writeback_sink?.type === 'same_as_source' ? `Same as source (${form.source_type})` :
                                    form.writeback_sink?.type === 'sql'            ? `SQL → ${form.writeback_sink.table || '(table not set)'}` :
                                    form.writeback_sink?.type === 'sharepoint'     ? `SharePoint → ${form.writeback_sink.workbook_path || '(not set)'}` :
                                    form.writeback_sink?.type === 'flow'           ? `Flow → ${flows.find(f => f.id === form.writeback_sink?.flow_id)?.name || '(not set)'}` :
                                    form.writeback_sink?.type === 'platform'       ? 'Platform-managed only' : '—'
                                } />

                                <Row label="Auto-Sync" value={
                                    form.writeback_trigger === 'scheduled'
                                        ? (form.writeback_schedule ? `ON — cron: ${form.writeback_schedule}` : 'ON — every 10 minutes')
                                        : 'OFF — manual sync only'
                                } />

                                {/* Schema — flat list of every selected column, exactly as configured in Step 2 */}
                                <div className="pt-2 border-t border-gray-200 dark:border-slate-700">
                                    <button
                                        type="button"
                                        onClick={() => setSchemaCollapsed(!schemaCollapsed)}
                                        className="w-full flex items-center justify-between py-1.5 group text-left focus:outline-none"
                                    >
                                        <p className="text-[10px] font-bold text-gray-400 dark:text-slate-500 uppercase tracking-widest">
                                            Schema — {selectedCols.length} of {allCols.length} columns included
                                        </p>
                                        <span className="text-[10px] font-semibold text-indigo-500 dark:text-indigo-400 group-hover:text-indigo-600 transition-colors flex items-center gap-1">
                                            {schemaCollapsed ? 'Show List (Expand) ▼' : 'Hide List (Collapse) ▲'}
                                        </span>
                                    </button>
                                    {!schemaCollapsed && (
                                        selectedCols.length === 0 ? (
                                            <p className="text-[11px] text-gray-400 mt-2">No columns selected — go back to Step 2</p>
                                        ) : (
                                            <div className="mt-3 overflow-x-auto max-h-[350px] overflow-y-auto rounded-xl border border-gray-150 dark:border-slate-800 p-1">
                                                <table className="w-full text-xs border-collapse">
                                                    <thead>
                                                        <tr className="text-[10px] text-gray-400 dark:text-slate-500 uppercase tracking-wide border-b border-gray-150 dark:border-slate-800">
                                                             <th className="text-left pb-1.5 font-semibold w-[35%] pl-2">Field</th>
                                                             <th className="text-left pb-1.5 font-semibold w-[25%] font-medium">Core Mapping</th>
                                                             <th className="text-left pb-1.5 font-semibold w-[20%] font-medium">Field Type</th>
                                                             <th className="text-left pb-1.5 font-semibold w-[20%] pr-2 font-medium">Access</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {selectedCols.map((r, i) => (
                                                            <tr key={r.source_field || i} className={i % 2 === 0 ? 'bg-white/60 dark:bg-slate-800/40 hover:bg-gray-50/60 dark:hover:bg-slate-850' : 'hover:bg-gray-50 dark:hover:bg-slate-800/30'}>
                                                                <td className="py-2 pr-2 font-semibold text-gray-800 dark:text-slate-200 pl-2">{r.source_field}</td>
                                                                <td className="py-2 pr-2 text-gray-500 dark:text-slate-400">{r.core_mapping || '—'}</td>
                                                                <td className="py-2 pr-2 text-gray-500 dark:text-slate-400">{r.field_type || 'As-Is'}</td>
                                                                <td className="py-2 pr-2">
                                                                    {r.is_read_only
                                                                        ? <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded text-[10px] font-bold">🔒 Read-Only</span>
                                                                        : <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 rounded text-[10px] font-bold">✏ Editable</span>
                                                                    }
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )
                                    )}
                                </div>
                            </div>
                            );
                        })()}



                        {/* ── Publish scope ── */}
                        <Field label="Publish Scope">
                            <div className="grid grid-cols-1 gap-3">
                                {[
                                    { v: 'draft',    icon: '📝', label: 'Save as Draft',     desc: 'Save configuration but do not expose to users yet. Only admins can see it.',      badge: 'Private',   badgeColor: 'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-300' },
                                    { v: 'internal', icon: '🏢', label: 'Publish Internally', desc: 'Visible to the roles and users configured in the Access step.',                   badge: 'Scoped',    badgeColor: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300' },
                                    { v: 'public',   icon: '🌐', label: 'Publish to All',     desc: 'Override the access configuration and make this worklist visible to every user.', badge: 'Open',      badgeColor: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' },
                                ].map(opt => (
                                    <button key={opt.v} type="button" onClick={() => patch('publish_scope', opt.v)}
                                        className={`text-left p-4 rounded-2xl border-2 transition-all ${
                                            (form.publish_scope || 'internal') === opt.v
                                                ? 'border-indigo-500 bg-indigo-50/50 dark:bg-indigo-900/20'
                                                : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600 bg-white dark:bg-slate-900'
                                        }`}>
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="flex items-center gap-2.5">
                                                <span className="text-xl">{opt.icon}</span>
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <p className="text-xs font-bold text-gray-800 dark:text-slate-200">{opt.label}</p>
                                                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold ${opt.badgeColor}`}>{opt.badge}</span>
                                                    </div>
                                                    <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-0.5 leading-relaxed">{opt.desc}</p>
                                                </div>
                                            </div>
                                            <div className={`mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                                                (form.publish_scope || 'internal') === opt.v ? 'border-indigo-500 bg-indigo-500' : 'border-gray-300 dark:border-slate-600'
                                            }`}>
                                                {(form.publish_scope || 'internal') === opt.v && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                                            </div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </Field>

                        {/* ── Preview CTA ── */}
                        {savedId && (
                            <div className="p-4 bg-gradient-to-r from-indigo-50 to-violet-50 dark:from-indigo-950/30 dark:to-violet-950/30 rounded-2xl border border-indigo-100 dark:border-indigo-900/40">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-xs font-bold text-indigo-700 dark:text-indigo-300">Preview Ready</p>
                                        <p className="text-[10px] text-indigo-500 dark:text-indigo-400 mt-0.5">See exactly how this worklist will appear to your users before publishing.</p>
                                    </div>
                                    <button type="button" onClick={() => setShowPreview(true)}
                                        className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold transition-all shadow-sm">
                                        <Eye className="w-3.5 h-3.5" /> Preview
                                    </button>
                                </div>
                            </div>
                        )}
                        {!savedId && (
                            <p className="text-[10px] text-gray-400 dark:text-slate-500 text-center">Save a draft first to unlock the Preview option.</p>
                        )}

                    </StepCard>
                )}

            </div>

                    {/* Footer nav */}
                    <div className="flex-none bg-white dark:bg-slate-900 border-t border-gray-100 dark:border-slate-800">
                        <div className="px-8 py-4 flex items-center justify-between">
                            {/* Left — Back + Save Draft */}
                            <div className="flex items-center gap-3">
                            <button disabled={step === 1} onClick={() => { setErrors({}); setStep(s => s - 1); }}
                                className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 dark:border-slate-700 text-sm font-medium text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800 disabled:opacity-40 transition-colors">
                                <ArrowLeft className="w-4 h-4" /> Back
                            </button>
                            {step < 5 && (
                                <button onClick={async () => {
                                    if (!validateStep(1)) {
                                        addNotification('Worklist Name and Source are required to save a draft', 'warning');
                                        return;
                                    }
                                    setSaving(true);
                                    try {
                                        const payload = { ...form };
                                        delete payload._wbFieldsOpen;
                                        if (payload.source_type === 'sharepoint' && payload.writeback_mode === 'sql_table') {
                                            payload.writeback_mode = 'platform_managed';
                                        }
                                        if (isEdit) {
                                            await interactiveFetch(`/worklists/${appId}`, token, { method: 'PATCH', body: JSON.stringify(payload) });
                                            addNotification('Draft saved successfully', 'success');
                                        } else {
                                            const res = await interactiveFetch('/worklists', token, { method: 'POST', body: JSON.stringify(payload) });
                                            addNotification('Draft created successfully', 'success');
                                            setSavedId(res.id);
                                            if (onDraftSaved) onDraftSaved(res.id);
                                        }
                                    } catch(e) {
                                        addNotification(`Save failed: ${e?.message || 'Unknown error'}`, 'error');
                                    } finally {
                                        setSaving(false);
                                    }
                                }} disabled={saving} className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 dark:border-slate-700 text-sm font-medium text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800 disabled:opacity-40 transition-colors">
                                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 text-indigo-500" />} Save Draft
                                </button>
                            )}
                            </div>
                            {/* Right — Next or Publish */}
                            {step < 5 ? (
                                <button onClick={handleNext}
                                    className="flex items-center gap-2 px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-semibold shadow-sm transition-all">
                                    Next <ArrowRight className="w-4 h-4" />
                                </button>
                            ) : (
                                <div className="flex items-center gap-3">
                                    {savedId && (
                                        <button onClick={() => setShowPreview(true)}
                                            className="flex items-center gap-2 px-4 py-2 rounded-xl border border-indigo-300 dark:border-indigo-700 text-sm font-semibold text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/30 transition-all">
                                            <Eye className="w-4 h-4" /> Preview
                                        </button>
                                    )}
                                    <button onClick={() => setShowConfirm(true)} disabled={saving}
                                        className="flex items-center gap-2 px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-bold shadow-md transition-all disabled:opacity-60">
                                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
                                        {form.publish_scope === 'draft'
                                            ? (isEdit ? 'Save Draft' : 'Create Draft')
                                            : (isEdit ? 'Save & Publish' : 'Create & Publish')}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>{/* end right column */}
            </div>{/* end sidebar+content layout */}
        </div>

        {/* ── Preview Modal ──────────────────────────────────────────── */}
        {showPreview && savedId && (
            <div className="fixed inset-0 z-50 flex flex-col bg-white dark:bg-slate-950">
                {/* Modal header */}
                <div className="flex-none flex items-center justify-between px-6 py-3 bg-white dark:bg-slate-900 border-b border-gray-100 dark:border-slate-800 shadow-sm">
                    <div className="flex items-center gap-3">
                        <button onClick={() => setShowPreview(false)}
                            className="flex items-center gap-2 px-3 py-1.5 rounded-xl border border-gray-200 dark:border-slate-700 text-xs font-semibold text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors">
                            <ArrowLeft className="w-3.5 h-3.5" /> Back to Editor
                        </button>
                        <div className="h-4 w-px bg-gray-200 dark:bg-slate-700" />
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded-full text-[11px] font-bold">
                            <Eye className="w-3 h-3" /> PREVIEW MODE
                        </span>
                        <p className="text-xs text-gray-400 dark:text-slate-500">This is exactly what your users will see</p>
                    </div>
                    <button
                        onClick={async () => {
                            setSaving(true);
                            try {
                                const payload = { 
                                    ...form, 
                                    status: 'published',
                                    publish_scope: (form.publish_scope === 'draft' || !form.publish_scope) ? 'internal' : form.publish_scope
                                };
                                delete payload._wbFieldsOpen;
                                if (isEdit) {
                                    await interactiveFetch(`/worklists/${savedId}`, token, { method: 'PATCH', body: JSON.stringify(payload) });
                                } else {
                                    await interactiveFetch(`/worklists/${savedId}`, token, { method: 'PATCH', body: JSON.stringify(payload) });
                                }
                                addNotification('Worklist published successfully 🎉', 'success');
                                setShowPreview(false);
                                if (onSaved) onSaved(savedId);
                            } catch(e) {
                                addNotification(`Publish failed: ${e?.message || 'Unknown error'}`, 'error');
                            } finally { setSaving(false); }
                        }}
                        disabled={saving}
                        className="flex items-center gap-2 px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-bold shadow-md transition-all disabled:opacity-60">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
                        Confirm &amp; Publish
                    </button>
                </div>
                {/* Runner embedded full height */}
                <div className="flex-1 overflow-hidden">
                    <WorklistRunnerPage appId={savedId} onBack={() => setShowPreview(false)} isPreview={true} />
                </div>
            </div>
        )}
        </>
    );
}

// ─── Helpers ────────────────────────────────────────────────────────────────
const input = 'w-full px-3.5 py-2.5 text-sm bg-gray-50/50 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/80 rounded-xl outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:text-slate-100 transition-all duration-200';

function StepCard({ title, desc, children }) {
    return (
        <div className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-md rounded-2xl border border-gray-100 dark:border-slate-800/80 p-6 space-y-5 shadow-[0_8px_30px_rgb(0,0,0,0.02)]">
            <div>
                <h2 className="text-base font-bold text-gray-900 dark:text-slate-100">{title}</h2>
                <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">{desc}</p>
            </div>
            {children}
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div className="space-y-1.5">
            <label className="block text-xs font-semibold text-gray-600 dark:text-slate-400 uppercase tracking-wider">{label}</label>
            {children}
        </div>
    );
}

function ColSelect({ cols, value, onChange }) {
    return (
        <select className={input} value={value} onChange={e => onChange(e.target.value)}>
            <option value="">— Not mapped —</option>
            {cols.map(c => <option key={c} value={c}>{c}</option>)}
            {!cols.includes(value) && value && <option value={value}>{value} (manual)</option>}
        </select>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex items-start gap-3">
            <span className="text-xs font-semibold text-gray-500 dark:text-slate-400 w-24 shrink-0">{label}</span>
            <span className="text-sm text-gray-900 dark:text-slate-100">{value || '—'}</span>
        </div>
    );
}
